// Force deploy: 2026-01-14 - Consolation Tournament Update
import React, { useState, useEffect, useContext } from 'react';
import axios from 'axios';
import { Link } from 'react-router-dom';
import { AuthContext } from '../App';
import { useWebSocket } from '../contexts/WebSocketContext';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import { Trophy, Users, Clock, AlertCircle, CheckCircle, Edit2, Loader } from 'lucide-react';
import { toast } from 'sonner';
import MatchSocial from './MatchSocial';
import SponsorBanner from './SponsorBanner';
import { shareElementAsImage, exportElementAsPdf } from '../utils/exportUtils';

const API = process.env.REACT_APP_BACKEND_URL || '';

const TournamentBracketInteractive = ({ tournamentId, tournamentType, category, tournamentName, tournamentDate }) => {
  const { user } = useContext(AuthContext);
  const { lastMessage } = useWebSocket();
  const [bracket, setBracket] = useState(null);
  const [loading, setLoading] = useState(true);
  const [selectedMatch, setSelectedMatch] = useState(null);
  const [isResultModalOpen, setIsResultModalOpen] = useState(false);
  const [playerRanks, setPlayerRanks] = useState({}); // מיפוי ID -> rank
  const [playerPoints, setPlayerPoints] = useState({}); // מיפוי ID -> points שקיבל בתחרות
  
  // Edit groups modal
  const [isEditGroupsModalOpen, setIsEditGroupsModalOpen] = useState(false);
  const [tournamentGroups, setTournamentGroups] = useState({});
  const [allTournamentUsers, setAllTournamentUsers] = useState([]);
  const [tournamentWaitingList, setTournamentWaitingList] = useState([]);
  const [tournamentRegistered, setTournamentRegistered] = useState([]);
  
  const isAdmin = ['shafirroy@icloud.com', 'shafirroy@gmail.com'].includes(user?.email);
  
  // Result input state - תלוי בסוג התחרות
  const initialSets = tournamentType === 'daily' 
    ? [{ player1_score: '', player2_score: '' }] // תחרות יומית - סט אחד
    : [
        { player1_score: '', player2_score: '' },
        { player1_score: '', player2_score: '' },
        { player1_score: '', player2_score: '' }
      ]; // תחרות נוקאאוט - 2-3 סטים
  
  const [sets, setSets] = useState(initialSets);
  const [winnerId, setWinnerId] = useState('');
  const [winType, setWinType] = useState('normal'); // normal | walkover | retirement
  const [validationErrors, setValidationErrors] = useState([]);
  
  // Player assignment state (for TBD matches)
  const [isPlayerAssignModalOpen, setIsPlayerAssignModalOpen] = useState(false);
  const [selectedMatchForPlayers, setSelectedMatchForPlayers] = useState(null);
  const [player1Name, setPlayer1Name] = useState('');
  const [player2Name, setPlayer2Name] = useState('');
  // For doubles - partner names
  const [partner1Name, setPartner1Name] = useState('');
  const [partner2Name, setPartner2Name] = useState('');
  
  // Edit bracket state (לעריכת הגרלה ידנית)
  const [isEditMatchModalOpen, setIsEditMatchModalOpen] = useState(false);
  const [selectedMatchForEdit, setSelectedMatchForEdit] = useState(null);
  const [editPlayer1Id, setEditPlayer1Id] = useState('');
  const [editPlayer2Id, setEditPlayer2Id] = useState('');
  const [editableRounds, setEditableRounds] = useState([]);
  const [consolationEditableRounds, setConsolationEditableRounds] = useState([]);
  const [consolationWaLinks, setConsolationWaLinks] = useState(null);
  const [searchPlayer1, setSearchPlayer1] = useState('');
  const [searchPlayer2, setSearchPlayer2] = useState('');
  const [savingEdit, setSavingEdit] = useState(false);
  
  // Swap mode state (להחלפת שחקנים בין משחקים)
  const [swapMode, setSwapMode] = useState(false);
  const [selectedForSwap, setSelectedForSwap] = useState(null); // {matchId, position, playerName}

  // WebSocket listener for real-time updates
  useEffect(() => {
    if (lastMessage && lastMessage.type === 'match_result') {
      if (lastMessage.tournament_id === tournamentId) {
        // Refresh bracket when any match result is updated
        fetchBracket();
        toast.success('הברקט עודכן בזמן אמת!', {
          description: 'תוצאה חדשה התקבלה'
        });
      }
    }
  }, [lastMessage, tournamentId]);

  useEffect(() => {
    if (tournamentId) {
      fetchBracket();
      if (isAdmin) {
        fetchEditableRounds();
      }
      // Auto-refresh every 60 seconds as backup
      const interval = setInterval(fetchBracket, 60000);
      return () => clearInterval(interval);
    }
  }, [tournamentId, isAdmin]);
  
  // טעינת הסיבובים הניתנים לעריכה
  const fetchEditableRounds = async () => {
    try {
      const token = localStorage.getItem('token') || sessionStorage.getItem('token');
      const response = await axios.get(`${API}/api/admin/tournaments/${tournamentId}/editable-rounds`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setEditableRounds(response.data.editable_rounds || []);
      setConsolationEditableRounds(response.data.consolation_editable || []);
    } catch (error) {
      console.error('Error fetching editable rounds:', error);
    }
  };

  // טעינת קישורי WhatsApp למשתתפי התנחומים (אדמין)
  const fetchConsolationWhatsapp = async () => {
    try {
      const token = localStorage.getItem('token') || sessionStorage.getItem('token');
      const response = await axios.get(`${API}/api/admin/tournaments/${tournamentId}/consolation-whatsapp`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      const targets = response.data.targets || [];
      setConsolationWaLinks(targets);
      if (targets.length === 0) {
        toast.info('אין משתתפים עם מספר טלפון בטורניר התנחומים');
      }
    } catch (error) {
      console.error('Error fetching consolation whatsapp:', error);
      toast.error('שגיאה בטעינת קישורי WhatsApp');
    }
  };
  
  // בדיקה אם משחק ניתן לעריכה
  const isMatchEditable = (match) => {
    if (!isAdmin) return false;
    if (match.completed) return false;
    
    // בתחרויות נוקאאוט - הכל תמיד פתוח לעריכה!
    if (tournamentType === 'knockout') return true;
    
    const round = match.round;
    
    // בדיקת סיבובים רגילים
    if (editableRounds.includes(round)) return true;
    
    // בדיקת סיבובי תנחומים - תמיד ניתנים לעריכה אם לא הושלמו
    if (consolationEditableRounds.includes(round)) return true;
    
    return false;
  };
  
  // בדיקה אם משחק זמין להחלפת שחקנים (גם משחקים שהושלמו)
  const isMatchSwappable = (match) => {
    if (!isAdmin) return false;
    if (!swapMode) return false;
    
    // בתחרויות נוקאאוט - כל המשבצות זמינות (גם ריקות - להזזה)
    if (tournamentType === 'knockout') {
      return true;
    }
    
    // במצב החלפה - כל המשחקים זמינים (כולל ריקים)
    return true;
  };
  
  // פתיחת מודל עריכת משחק
  const openEditMatchModal = (match) => {
    setSelectedMatchForEdit(match);
    setEditPlayer1Id(match.player1_id || '');
    setEditPlayer2Id(match.player2_id || '');
    setSearchPlayer1('');
    setSearchPlayer2('');
    setIsEditMatchModalOpen(true);
  };
  
  // שמירת עריכת משחק
  const saveEditMatch = async () => {
    if (!selectedMatchForEdit) return;
    
    setSavingEdit(true);
    try {
      const token = localStorage.getItem('token') || sessionStorage.getItem('token');
      await axios.put(`${API}/api/admin/matches/${selectedMatchForEdit.id}/edit-players`, {
        player1_id: editPlayer1Id || null,
        player2_id: editPlayer2Id || null
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      toast.success('המשחק עודכן בהצלחה!');
      setIsEditMatchModalOpen(false);
      fetchBracket();
    } catch (error) {
      console.error('Error saving match edit:', error);
      toast.error(error.response?.data?.detail || 'שגיאה בעדכון המשחק');
    } finally {
      setSavingEdit(false);
    }
  };

  // פונקציה להחלפת שחקנים בין משחקים
  const handleSwapClick = (matchId, position, playerName) => {
    const isEmpty = !playerName || playerName === 'ממתין' || playerName === 'TBD';
    
    if (!selectedForSwap) {
      if (isEmpty) {
        toast.error('בחר קודם שחקן להזזה');
        return;
      }
      // בחירה ראשונה - חייב להיות שחקן אמיתי
      setSelectedForSwap({ matchId, position, playerName });
      toast.info(`נבחר: ${playerName}`, { description: 'כעת לחץ על משבצת יעד (ריקה או שחקן אחר)' });
    } else if (selectedForSwap.matchId === matchId && selectedForSwap.position === position) {
      // ביטול הבחירה
      setSelectedForSwap(null);
      toast.info('הבחירה בוטלה');
    } else {
      // ביצוע ההחלפה/הזזה
      performSwap(matchId, position, playerName || 'ממתין');
    }
  };
  
  const performSwap = async (match2Id, position2, player2Name) => {
    try {
      const token = localStorage.getItem('token') || sessionStorage.getItem('token');
      await axios.post(`${API}/api/admin/matches/swap-players`, {
        match1_id: selectedForSwap.matchId,
        match1_position: selectedForSwap.position,
        match2_id: match2Id,
        match2_position: position2
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      const isEmpty = !player2Name || player2Name === 'ממתין' || player2Name === 'TBD';
      if (isEmpty) {
        toast.success(`${selectedForSwap.playerName} הועבר למשבצת חדשה`);
      } else {
        toast.success(`הוחלפו: ${selectedForSwap.playerName} ↔ ${player2Name}`);
      }
      setSelectedForSwap(null);
      setSwapMode(false);
      fetchBracket();
    } catch (error) {
      console.error('Error swapping players:', error);
      toast.error(error.response?.data?.detail || 'שגיאה בהחלפת שחקנים');
    }
  };
  
  const cancelSwapMode = () => {
    setSwapMode(false);
    setSelectedForSwap(null);
    toast.info('מצב החלפה בוטל');
  };

  const clearSlot = async (matchId, position, playerName) => {
    if (!confirm(`למחוק את ${playerName} מהמשבצת?`)) return;
    try {
      const token = localStorage.getItem('token') || sessionStorage.getItem('token');
      await axios.post(`${API}/api/admin/matches/clear-slot`, {
        match_id: matchId,
        position: position
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success(`${playerName} הוסר מהמשבצת`);
      fetchBracket();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'שגיאה במחיקה');
    }
  };


  const fetchBracket = async () => {
    try {
      const response = await axios.get(`${API}/api/tournaments/${tournamentId}/bracket`);
      setBracket(response.data);
      
      // שמירת נתוני groups לעריכה
      if (response.data.tournament?.groups) {
        setTournamentGroups(response.data.tournament.groups);
      }
      
      // מיפוי דירוג השחקן בתוך התחרות (seed) - מוצג ליד כל שחקן
      const seedsMap = response.data.tournament?.seeds || {};
      const ranks = {};
      if (Object.keys(seedsMap).length > 0) {
        // השתמש בדירוג בתוך התחרות (seed) לכל השחקנים
        Object.entries(seedsMap).forEach(([pid, seed]) => {
          if (seed) ranks[pid] = seed;
        });
      } else if (response.data.players) {
        // גיבוי לתחרויות ישנות ללא מפת seeds - דירוג כללי לשמונה הראשונים
        response.data.players.forEach(player => {
          if (player.rank && player.rank <= 8) {
            ranks[player.id] = player.rank;
          }
        });
      }
      if (response.data.players) {
        setAllTournamentUsers(response.data.players);
      }
      setPlayerRanks(ranks);
      
      // שמירת הנקודות שכל שחקן קיבל בתחרות (אם התחרות הסתיימה)
      if (response.data.tournament?.player_points) {
        setPlayerPoints(response.data.tournament.player_points);
      }
    } catch (error) {
      console.error('Error fetching bracket:', error);
      toast.error('שגיאה בטעינת הברקט');
    } finally {
      setLoading(false);
    }
  };

  // פתיחת Modal עריכת בתים
  const openEditGroupsModal = async () => {
    try {
      // טעינת נתונים עדכניים
      const response = await axios.get(`${API}/api/tournaments/${tournamentId}`);
      let groups = response.data.groups || {};
      
      // אם ה-groups ריק, בנה אותו מהמשחקים
      if (Object.keys(groups).length === 0) {
        const bracketResponse = await axios.get(`${API}/api/tournaments/${tournamentId}/bracket`);
        const matches = bracketResponse.data.matches || [];
        
        // בנה groups מהמשחקים
        const builtGroups = {};
        matches.forEach(match => {
          const groupName = match.group_name;
          if (groupName && groupName.match(/^[A-D]$/)) {
            if (!builtGroups[groupName]) {
              builtGroups[groupName] = new Set();
            }
            if (match.player1_id && match.player1_id !== 'TBD') {
              builtGroups[groupName].add(match.player1_id);
            }
            if (match.player2_id && match.player2_id !== 'TBD') {
              builtGroups[groupName].add(match.player2_id);
            }
          }
        });
        
        // המר Sets לאריים
        groups = {};
        Object.keys(builtGroups).sort().forEach(key => {
          groups[key] = Array.from(builtGroups[key]);
        });
      }
      
      setTournamentGroups(groups);
      setTournamentWaitingList(response.data.waiting_list || []);
      setTournamentRegistered(response.data.registered_players || []);
      
      // טעינת כל המשתמשים
      const usersResponse = await axios.get(`${API}/users`);
      setAllTournamentUsers(usersResponse.data);
      
      setIsEditGroupsModalOpen(true);
    } catch (error) {
      toast.error('שגיאה בטעינת נתונים');
    }
  };

  // העברת שחקן מבית לבית
  const movePlayerToGroup = async (userId, fromGroup, toGroup) => {
    try {
      const token = localStorage.getItem('token') || sessionStorage.getItem('token');
      await axios.post(
        `${API}/api/admin/tournaments/${tournamentId}/move-player-between-groups`,
        {
          user_id: userId,
          from_group: fromGroup,
          to_group: toGroup
        },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success('השחקן הועבר בהצלחה!');
      
      // רענון נתונים
      const response = await axios.get(`${API}/api/tournaments/${tournamentId}`);
      if (response.data.groups) {
        setTournamentGroups(response.data.groups);
      }
      fetchBracket();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'שגיאה בהעברת שחקן');
    }
  };

  // הסרת שחקן מבית
  const removePlayerFromGroup = async (userId, groupName) => {
    try {
      const token = localStorage.getItem('token') || sessionStorage.getItem('token');
      await axios.post(
        `${API}/api/admin/tournaments/${tournamentId}/remove-player-from-group`,
        {
          user_id: userId,
          group_name: groupName
        },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success('השחקן הוסר מהבית!');
      
      // רענון נתונים
      const response = await axios.get(`${API}/api/tournaments/${tournamentId}`);
      if (response.data.groups) {
        setTournamentGroups(response.data.groups);
      }
      fetchBracket();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'שגיאה בהסרת שחקן');
    }
  };

  // הוספת שחקן לבית
  const addPlayerToGroup = async (userId, groupName) => {
    try {
      const token = localStorage.getItem('token') || sessionStorage.getItem('token');
      await axios.post(
        `${API}/api/admin/tournaments/${tournamentId}/add-player-to-group`,
        {
          user_id: userId,
          group_name: groupName
        },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success('השחקן נוסף לבית!');
      
      // רענון נתונים
      const response = await axios.get(`${API}/api/tournaments/${tournamentId}`);
      if (response.data.groups) {
        setTournamentGroups(response.data.groups);
      }
      fetchBracket();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'שגיאה בהוספת שחקן');
    }
  };

  const validateSets = () => {
    const errors = [];
    
    // Filter valid sets (both scores filled)
    const validSets = sets.filter(set => set.player1_score !== '' && set.player2_score !== '');
    
    // תחרות יומית - 1 סט בלבד
    // תחרות נוקאאוט - לפחות 2 סטים
    const minSets = tournamentType === 'daily' ? 1 : 2;
    const maxSets = tournamentType === 'daily' ? 1 : 3;
    
    if (validSets.length < minSets) {
      const msg = tournamentType === 'daily' 
        ? 'נא להזין סט אחד' 
        : 'נא להזין לפחות 2 סטים';
      errors.push(msg);
      return { isValid: false, errors };
    }
    
    if (validSets.length > maxSets) {
      const msg = tournamentType === 'daily' 
        ? 'תחרות יומית - סט אחד בלבד' 
        : 'מקסימום 3 סטים';
      errors.push(msg);
      return { isValid: false, errors };
    }
    
    let player1SetsWon = 0;
    let player2SetsWon = 0;
    
    validSets.forEach((set, idx) => {
      const p1 = parseInt(set.player1_score);
      const p2 = parseInt(set.player2_score);
      
      // Basic validation
      if (p1 < 0 || p2 < 0) {
        errors.push(`סט ${idx + 1}: ניקוד לא יכול להיות שלילי`);
        return;
      }
      
      const isThirdSetEarly = idx === 2;
      const isSuperTb = isThirdSetEarly && tournamentType === 'knockout';
      const maxPerSet = isSuperTb ? 20 : 7;
      if (p1 > maxPerSet || p2 > maxPerSet) {
        errors.push(`סט ${idx + 1}: ניקוד מקסימלי הוא ${maxPerSet}${isSuperTb ? ' (סופר טייברייק)' : ''}`);
        return;
      }
      
      if (p1 === p2) {
        errors.push(`סט ${idx + 1}: אין תוצאה שווה בסט`);
        return;
      }
      
      // Count sets won
      if (p1 > p2) player1SetsWon++;
      else player2SetsWon++;
      
      // Tennis set validation
      const maxScore = Math.max(p1, p2);
      const minScore = Math.min(p1, p2);
      const isThirdSet = idx === 2;
      
      if (isThirdSet && tournamentType === 'knockout') {
        // מערכה שלישית בנוקאאוט - סופר טייברייק (מינ׳ 10, מקס׳ 20)
        // תוצאות תקינות: 10-8, 11-9, 15-13, 20-18, וכו׳ — דורש הפרש 2+ או הגעה ל-20
        if (maxScore > 20) {
          errors.push(`סט ${idx + 1}: מערכה שלישית יכולה להיות עד 20 נקודות`);
        } else if (maxScore < 10) {
          errors.push(`סט ${idx + 1}: המנצח בסופר טייברייק חייב להגיע לפחות ל-10`);
        } else if (maxScore === minScore) {
          errors.push(`סט ${idx + 1}: תיקו לא אפשרי`);
        } else if (maxScore < 20 && (maxScore - minScore) < 2) {
          errors.push(`סט ${idx + 1}: סופר טייברייק דורש הפרש של 2 נקודות (למשל 10-8, 11-9) או סיום ב-20`);
        }
        // תוצאות תקינות: 10-8, 11-9, 15-13, 20-18, 20-19
      } else if (maxScore === 6) {
        if (minScore > 4) {
          errors.push(`סט ${idx + 1}: סט של 6 חייב להיות עד 4 למפסיד (למשל 6-4 או 6-3)`);
        }
      } else if (maxScore === 7) {
        if (minScore !== 5 && minScore !== 6) {
          errors.push(`סט ${idx + 1}: סט של 7 חייב להיות 7-5 או 7-6 (טאי-ברייק)`);
        }
      } else if (maxScore < 6) {
        errors.push(`סט ${idx + 1}: סט חייב להסתיים לפחות ב-6 או 7`);
      }
    });
    
    // Check if winner won required sets
    if (winnerId && selectedMatch) {
      const isPlayer1Winner = winnerId === selectedMatch.player1_id;
      const setsWonByWinner = isPlayer1Winner ? player1SetsWon : player2SetsWon;
      
      const requiredSets = tournamentType === 'daily' ? 1 : 2;
      const msg = tournamentType === 'daily' 
        ? 'השחקן שנבחר כמנצח חייב לנצח את הסט' 
        : 'השחקן שנבחר כמנצח חייב לנצח לפחות 2 סטים';
      
      if (setsWonByWinner < requiredSets) {
        errors.push(msg);
      }
    }
    
    return { isValid: errors.length === 0, errors };
  };

  const handleMatchClick = (match) => {
    // Check if user can enter results
    if (!user) {
      toast.error('עליך להיות מחובר כדי להזין תוצאות');
      return;
    }
    
    const isAdmin = user.is_admin;
    const isPlayerInMatch = user.id === match.player1_id || user.id === match.player2_id;
    
    // אם המשחק מושלם, רק אדמין יכול לערוך
    if (match.completed && !isAdmin) {
      toast.error('רק מנהל יכול לערוך תוצאה קיימת');
      return;
    }
    
    // אם המשחק לא מושלם, בדוק הרשאות
    if (!match.completed && !isAdmin && !isPlayerInMatch) {
      toast.error('רק מנהלים או שחקנים במשחק יכולים להזין תוצאה');
      return;
    }
    
    // Open result entry modal
    setSelectedMatch(match);
    setIsResultModalOpen(true);
    
    // אם המשחק מושלם, טען את התוצאות הקיימות
    if (match.completed && match.sets && match.sets.length > 0) {
      const existingSets = match.sets.map(set => ({
        player1_score: String(set.player1_games || set.player1_score || ''),
        player2_score: String(set.player2_games || set.player2_score || '')
      }));
      
      // הוסף סטים ריקים אם צריך (לנוקאאוט)
      while (existingSets.length < 3 && tournamentType !== 'daily') {
        existingSets.push({ player1_score: '', player2_score: '' });
      }
      
      setSets(existingSets);
      setWinnerId(match.winner_id || '');
      setWinType(match.win_type || 'normal');
    } else {
      // Reset form based on tournament type
      const resetSets = tournamentType === 'daily' 
        ? [{ player1_score: '', player2_score: '' }]
        : [
            { player1_score: '', player2_score: '' },
            { player1_score: '', player2_score: '' },
            { player1_score: '', player2_score: '' }
          ];
      
      setSets(resetSets);
      setWinnerId('');
      setWinType('normal');
    }
    
    setValidationErrors([]);
  };
  
  const handleSetPlayersClick = (match) => {
    setSelectedMatchForPlayers(match);
    // Parse player names for doubles (format: "שחקן1 / שחקן2")
    const p1Parts = (match.player1_name || '').split(' / ');
    const p2Parts = (match.player2_name || '').split(' / ');
    
    setPlayer1Name(match.player1_name === 'ממתין' ? '' : (p1Parts[0] || ''));
    setPartner1Name(p1Parts[1] || '');
    setPlayer2Name(match.player2_name === 'ממתין' ? '' : (p2Parts[0] || ''));
    setPartner2Name(p2Parts[1] || '');
    setIsPlayerAssignModalOpen(true);
  };
  
  // Check if tournament is doubles
  const isDoubles = category?.includes('זוגות');
  
  const handleSavePlayers = async () => {
    // Allow saving if at least one player name is entered
    const hasPlayer1 = player1Name.trim() !== '';
    const hasPlayer2 = player2Name.trim() !== '';
    
    if (!hasPlayer1 && !hasPlayer2) {
      toast.error('נא להזין לפחות שם שחקן אחד');
      return;
    }
    
    // For doubles, require partner names for the players that are being set
    if (isDoubles) {
      if (hasPlayer1 && !partner1Name.trim()) {
        toast.error('נא להזין שם שותף לזוג 1');
        return;
      }
      if (hasPlayer2 && !partner2Name.trim()) {
        toast.error('נא להזין שם שותף לזוג 2');
        return;
      }
    }
    
    try {
      const token = localStorage.getItem('token') || sessionStorage.getItem('token');
      
      // Build player names (for doubles, combine with partner)
      const finalPlayer1Name = hasPlayer1 ? (isDoubles 
        ? `${player1Name.trim()} / ${partner1Name.trim()}`
        : player1Name.trim()) : null;
      const finalPlayer2Name = hasPlayer2 ? (isDoubles
        ? `${player2Name.trim()} / ${partner2Name.trim()}`
        : player2Name.trim()) : null;
      
      // חפש את ה-ID של השחקנים אם הם קיימים במערכת
      const player1 = allTournamentUsers.find(u => u.full_name === player1Name.trim());
      const player2 = allTournamentUsers.find(u => u.full_name === player2Name.trim());
      
      // אם זה משחק ריק, צריך ליצור אותו קודם
      if (selectedMatchForPlayers.isEmpty) {
        // צור משחק חדש
        try {
          await axios.post(
            `${API}/api/admin/tournaments/${tournamentId}/create-match`,
            {
              round: selectedMatchForPlayers.round,
              position: selectedMatchForPlayers.position,
              player1_id: player1?.id || `manual_${Date.now()}_1`,
              player1_name: finalPlayer1Name,
              player2_id: player2?.id || `manual_${Date.now()}_2`,
              player2_name: finalPlayer2Name
            },
            {
              headers: { Authorization: `Bearer ${token}` }
            }
          );
          
          toast.success('המשחק נוצר בהצלחה!');
          setIsPlayerAssignModalOpen(false);
          setPlayer1Name('');
          setPlayer2Name('');
          setPartner1Name('');
          setPartner2Name('');
          fetchBracket();
          return;
        } catch (err) {
          console.error('Error creating match:', err);
          toast.error(err.response?.data?.detail || 'שגיאה ביצירת המשחק');
          return;
        }
      }
      
      // נסה להשתמש ב-API לעריכת שחקנים
      try {
        // For players found in system, use edit-players with real IDs
        // For external players, use set-manual-player which creates proper manual_ IDs
        const player1IsExternal = hasPlayer1 && !player1;
        const player2IsExternal = hasPlayer2 && !player2;
        
        // If both players are in the system, use edit-players
        if (!player1IsExternal && !player2IsExternal) {
          await axios.put(
            `${API}/api/admin/matches/${selectedMatchForPlayers.id}/edit-players`,
            {
              player1_id: player1?.id || null,
              player1_name: finalPlayer1Name,
              player2_id: player2?.id || null,
              player2_name: finalPlayer2Name
            },
            {
              headers: { Authorization: `Bearer ${token}` }
            }
          );
        } else {
          // Use set-manual-player for external players (creates proper manual_ IDs)
          if (hasPlayer1) {
            if (player1) {
              // System player - use edit endpoint for this one
              await axios.put(
                `${API}/api/admin/matches/${selectedMatchForPlayers.id}/edit-players`,
                { player1_id: player1.id, player1_name: finalPlayer1Name },
                { headers: { Authorization: `Bearer ${token}` } }
              );
            } else {
              await axios.post(
                `${API}/api/admin/matches/${selectedMatchForPlayers.id}/set-manual-player`,
                { player_name: finalPlayer1Name, position: 1 },
                { headers: { Authorization: `Bearer ${token}` } }
              );
            }
          }
          if (hasPlayer2) {
            if (player2) {
              await axios.put(
                `${API}/api/admin/matches/${selectedMatchForPlayers.id}/edit-players`,
                { player2_id: player2.id, player2_name: finalPlayer2Name },
                { headers: { Authorization: `Bearer ${token}` } }
              );
            } else {
              await axios.post(
                `${API}/api/admin/matches/${selectedMatchForPlayers.id}/set-manual-player`,
                { player_name: finalPlayer2Name, position: 2 },
                { headers: { Authorization: `Bearer ${token}` } }
              );
            }
          }
        }
        
        toast.success('השחקנים עודכנו בהצלחה!');
        setIsPlayerAssignModalOpen(false);
        setPlayer1Name('');
        setPlayer2Name('');
        setPartner1Name('');
        setPartner2Name('');
        fetchBracket();
        return;
      } catch (err) {
        console.error('Error updating players:', err);
        toast.error(err.response?.data?.detail || 'שגיאה בעדכון השחקנים');
      }
    } catch (error) {
      console.error('Error setting players:', error);
      toast.error(error.response?.data?.detail || 'שגיאה בשמירת שמות השחקנים');
    }
  };

  const handleSubmitResult = async () => {
    if (!winnerId) {
      toast.error('נא לבחור מנצח');
      return;
    }

    // W.O. / פרישה — דילוג על ולידציה של סטים
    const isSpecial = winType === 'walkover' || winType === 'retirement';

    if (!isSpecial) {
      // Client-side validation
      const { isValid, errors } = validateSets();
      setValidationErrors(errors);
      
      if (!isValid) {
        toast.error('שגיאות בולידציה', {
          description: errors[0] // Show first error
        });
        return;
      }
    } else {
      setValidationErrors([]);
    }

    // Validate sets
    const validSets = isSpecial
      ? sets.filter(set => set.player1_score !== '' && set.player2_score !== '')
      : sets.filter(set => set.player1_score && set.player2_score);

    try {
      await axios.put(
        `${API}/api/matches/${selectedMatch.id}/submit-result`,
        {
          sets: validSets.map(set => ({
            player1_games: parseInt(set.player1_score) || 0,
            player2_games: parseInt(set.player2_score) || 0
          })),
          winner_id: winnerId,
          completed: true,
          win_type: winType,
        },
        {
          headers: {
            Authorization: `Bearer ${localStorage.getItem('token') || sessionStorage.getItem('token')}`
          }
        }
      );

      const successDesc = winType === 'walkover'
        ? 'נשמר כ-W.O. (עלה אוטומטית)'
        : winType === 'retirement'
          ? 'נשמר כפרישה'
          : 'המנצח יתקדם אוטומטית לשלב הבא';
      toast.success('התוצאה נשמרה בהצלחה!', { description: successDesc });
      setIsResultModalOpen(false);
      fetchBracket(); // Refresh bracket
    } catch (error) {
      console.error('Error submitting result:', error);
      const status = error.response?.status;
      const serverDetail = error.response?.data?.detail;
      const fallback = !error.response
        ? 'אין תקשורת עם השרת — בדוק חיבור אינטרנט'
        : `שגיאה ${status || ''} - ${serverDetail || 'לא ידוע. נסה שוב או רענן את הדף'}`;
      toast.error('שגיאה בשמירת התוצאה', {
        description: serverDetail || fallback,
        duration: 8000
      });
    }
  };

  const renderMatch = (match) => {
    if (!match) {
      return (
        <div className="bg-gray-100 p-3 rounded-lg text-center text-gray-400 text-sm">
          ממתין לשחקנים
        </div>
      );
    }

    const isAdmin = user?.is_admin;
    const isPlayerInMatch = user && (user.id === match.player1_id || user.id === match.player2_id);
    // כל משתמש מחובר יכול להזין תוצאות
    const canSubmitResult = user !== null;
    // רק אדמין או שחקן במשחק יכולים לערוך שחקנים
    const canEditPlayers = isAdmin || isPlayerInMatch;
    const isClickable = !match.completed && user && canSubmitResult;
    
    // Check if this is an empty placeholder match
    const isEmptyMatch = match.isEmpty === true;
    
    // Check if players have real names (not just TBD/ממתין)
    const hasRealPlayer1Name = match.player1_name && 
                                match.player1_name !== 'TBD' && 
                                match.player1_name !== 'ממתין' &&
                                match.player1_name.trim() !== '';
    const hasRealPlayer2Name = match.player2_name && 
                                match.player2_name !== 'TBD' && 
                                match.player2_name !== 'ממתין' &&
                                match.player2_name.trim() !== '';
    
    // Players are undefined only if BOTH name and ID are missing/TBD
    // If there's a real name, allow result entry even if ID is TBD
    const isPlayersUndefined = isEmptyMatch || 
                               !hasRealPlayer1Name || 
                               !hasRealPlayer2Name;
    const canSetPlayers = isAdmin && isPlayersUndefined;
    
    return (
      <div
        id={`match-card-${match.id}`}
        className={`bg-white border-2 rounded-xl p-2 md:p-3 text-sm md:text-base transition-all ${
          isClickable && canSubmitResult ? 'cursor-pointer hover:border-emerald-500 hover:shadow-lg hover:shadow-emerald-100 active:scale-[0.98]' : ''
        } ${match.completed ? 'border-emerald-300 shadow-md shadow-emerald-100' : isPlayersUndefined ? 'border-orange-300' : 'border-gray-200 shadow-sm'}
        ${swapMode && isMatchSwappable(match) ? 'ring-2 ring-purple-400' : ''}`}
        onClick={() => {
          if (canSetPlayers && !swapMode) {
            // Open player assignment dialog
            handleSetPlayersClick(match);
          } else if (isClickable && canSubmitResult && !swapMode && !isPlayersUndefined) {
            handleMatchClick(match);
          }
        }}
      >
        <div className="space-y-1">
          {/* Player 1 */}
          <div className={`flex items-center justify-between rounded-lg px-2 py-2 ${
            match.winner_id === match.player1_id ? 'font-extrabold text-emerald-700 bg-emerald-50' : 'font-semibold text-slate-800'
          } ${swapMode && selectedForSwap?.matchId === match.id && selectedForSwap?.position === 1 ? 'bg-purple-200 rounded px-1' : ''}`}>
            {swapMode && isMatchSwappable(match) ? (
              <>
              <button
                onClick={(e) => {
                  e.stopPropagation();
                  handleSwapClick(match.id, 1, match.player1_name);
                }}
                className={`truncate flex-1 text-right px-2 py-1 rounded transition-colors ${
                  selectedForSwap?.matchId === match.id && selectedForSwap?.position === 1
                    ? 'bg-purple-500 text-white'
                    : (!match.player1_id || match.player1_id === 'TBD' || match.player1_id === 'ממתין')
                      ? 'bg-green-100 hover:bg-green-200 text-green-800 border-2 border-dashed border-green-400'
                      : 'bg-purple-100 hover:bg-purple-200 text-purple-800'
                }`}
              >
                <span>{match.player1_name || 'ממתין'}</span>
                {playerRanks[match.player1_id] && (
                  <span className="text-xs font-bold ml-1">(#{playerRanks[match.player1_id]})</span>
                )}
              </button>
              {match.player1_id && match.player1_id !== 'TBD' && match.player1_id !== 'ממתין' && (
                <button
                  onClick={(e) => { e.stopPropagation(); clearSlot(match.id, 1, match.player1_name); }}
                  className="ml-1 w-6 h-6 flex items-center justify-center rounded-full bg-red-500 hover:bg-red-600 text-white text-xs font-bold shrink-0"
                  title="רוקן משבצת"
                >✕</button>
              )}
              </>
            ) : (
              <span className="truncate flex-1 text-base">
                {match.player1_id && match.player1_id !== 'TBD' && match.player1_id !== 'ממתין' && !match.player1_id.startsWith('manual_') ? (
                  <Link 
                    to={`/profile/${match.player1_id}`}
                    onClick={(e) => e.stopPropagation()}
                    className="hover:text-blue-600 hover:underline transition-colors cursor-pointer tracking-tight"
                  >
                    {match.player1_name || 'TBD'}
                  </Link>
                ) : (
                  <span className="tracking-tight">{match.player1_name || 'TBD'}</span>
                )}
                {playerRanks[match.player1_id] && (
                  <span className="text-[10px] text-blue-600 font-bold ml-1 bg-blue-50 px-1.5 py-0.5 rounded">#{playerRanks[match.player1_id]}</span>
                )}
                {playerPoints[match.player1_id] && (
                  <span className="text-[10px] text-green-600 font-bold ml-1 bg-green-50 px-1.5 py-0.5 rounded">+{playerPoints[match.player1_id]}</span>
                )}
                {match.winner_id === match.player1_id && (
                  <CheckCircle className="inline-block w-4 h-4 ml-1 text-emerald-600" />
                )}
              </span>
            )}
            {!swapMode && match.completed && match.sets && match.sets.length > 0 && (
              <div className="flex gap-1 text-sm ml-2">
                {match.sets.map((set, i) => (
                  <span 
                    key={i} 
                    className={`w-7 h-7 flex items-center justify-center rounded-md font-mono ${
                      (set.player1_games || set.player1_score || 0) > (set.player2_games || set.player2_score || 0)
                        ? 'bg-emerald-500 text-white font-bold shadow-sm' 
                        : 'bg-gray-100 text-gray-500'
                    }`}
                  >
                    {set.player1_games || set.player1_score || 0}
                  </span>
                ))}
              </div>
            )}
          </div>
          
          {/* VS Divider */}
          <div className="relative flex items-center justify-center py-0.5">
            <div className="absolute inset-x-2 h-px bg-gradient-to-r from-transparent via-emerald-200 to-transparent" />
            <span className="relative bg-white px-2 text-[10px] text-emerald-600 font-bold tracking-widest">VS</span>
          </div>
          
          {/* Player 2 */}
          <div className={`flex items-center justify-between rounded-lg px-2 py-2 ${
            match.winner_id === match.player2_id ? 'font-extrabold text-emerald-700 bg-emerald-50' : 'font-semibold text-slate-800'
          } ${swapMode && selectedForSwap?.matchId === match.id && selectedForSwap?.position === 2 ? 'bg-purple-200 rounded px-1' : ''}`}>
            {swapMode && isMatchSwappable(match) ? (
              <>
              <button
                onClick={(e) => {
                  e.stopPropagation();
                  handleSwapClick(match.id, 2, match.player2_name);
                }}
                className={`truncate flex-1 text-right px-2 py-1 rounded transition-colors ${
                  selectedForSwap?.matchId === match.id && selectedForSwap?.position === 2
                    ? 'bg-purple-500 text-white'
                    : (!match.player2_id || match.player2_id === 'TBD' || match.player2_id === 'ממתין')
                      ? 'bg-green-100 hover:bg-green-200 text-green-800 border-2 border-dashed border-green-400'
                      : 'bg-purple-100 hover:bg-purple-200 text-purple-800'
                }`}
              >
                <span>{match.player2_name || 'ממתין'}</span>
                {playerRanks[match.player2_id] && (
                  <span className="text-xs font-bold ml-1">(#{playerRanks[match.player2_id]})</span>
                )}
              </button>
              {match.player2_id && match.player2_id !== 'TBD' && match.player2_id !== 'ממתין' && (
                <button
                  onClick={(e) => { e.stopPropagation(); clearSlot(match.id, 2, match.player2_name); }}
                  className="ml-1 w-6 h-6 flex items-center justify-center rounded-full bg-red-500 hover:bg-red-600 text-white text-xs font-bold shrink-0"
                  title="רוקן משבצת"
                >✕</button>
              )}
              </>
            ) : (
              <span className="truncate flex-1 text-base">
                {match.player2_id && match.player2_id !== 'TBD' && match.player2_id !== 'ממתין' && !match.player2_id.startsWith('manual_') ? (
                  <Link 
                    to={`/profile/${match.player2_id}`}
                    onClick={(e) => e.stopPropagation()}
                    className="hover:text-blue-600 hover:underline transition-colors cursor-pointer tracking-tight"
                  >
                    {match.player2_name || 'שחקן 2'}
                  </Link>
                ) : (
                  <span className="tracking-tight">{match.player2_name || 'שחקן 2'}</span>
                )}
                {playerRanks[match.player2_id] && (
                  <span className="text-[10px] text-blue-600 font-bold ml-1 bg-blue-50 px-1.5 py-0.5 rounded">#{playerRanks[match.player2_id]}</span>
                )}
                {playerPoints[match.player2_id] && (
                  <span className="text-xs text-green-600 font-bold ml-1">+{playerPoints[match.player2_id]} נק</span>
                )}
                {match.winner_id === match.player2_id && (
                  <CheckCircle className="inline-block w-4 h-4 ml-1 text-emerald-600" />
                )}
              </span>
            )}
            {!swapMode && match.completed && match.sets && match.sets.length > 0 && (
              <div className="flex gap-1 text-sm ml-2">
                {match.sets.map((set, i) => (
                  <span 
                    key={i} 
                    className={`w-7 h-7 flex items-center justify-center rounded-md font-mono ${
                      (set.player2_games || set.player2_score || 0) > (set.player1_games || set.player1_score || 0)
                        ? 'bg-emerald-500 text-white font-bold shadow-sm' 
                        : 'bg-gray-100 text-gray-500'
                    }`}
                  >
                    {set.player2_games || set.player2_score || 0}
                  </span>
                ))}
              </div>
            )}
          </div>
        </div>

        {/* הודעה ברורה לפרישה / W.O. */}
        {match.completed && (match.win_type === 'walkover' || match.win_type === 'retirement') && (() => {
          const winnerName = match.winner_id === match.player1_id ? match.player1_name : match.winner_id === match.player2_id ? match.player2_name : 'המנצח';
          const loserName = match.winner_id === match.player1_id ? match.player2_name : match.winner_id === match.player2_id ? match.player1_name : 'היריב';
          const isWalkover = match.win_type === 'walkover';
          return (
            <div
              data-testid={`win-type-message-${match.id}`}
              className={`mt-2 px-3 py-2 rounded-lg text-xs text-center font-medium border ${
                isWalkover
                  ? 'bg-amber-50 border-amber-200 text-amber-900'
                  : 'bg-orange-50 border-orange-200 text-orange-900'
              }`}
            >
              {isWalkover ? (
                <>⚡ <strong>{winnerName}</strong> עלה אוטומטית — <strong>{loserName}</strong> לא הופיע למשחק</>
              ) : (
                <>🤕 <strong>{winnerName}</strong> ניצח עקב פרישה של <strong>{loserName}</strong></>
              )}
            </div>
          );
        })()}

        {match.completed && (
          <div className="mt-2 flex items-center justify-center gap-2 flex-wrap">
            <span className="text-xs bg-green-100 text-green-800 px-2 py-1 rounded">
              ✓ הסתיים
            </span>
            {match.win_type === 'walkover' && (
              <span className="text-xs bg-amber-100 text-amber-900 px-2 py-1 rounded font-bold" data-testid={`badge-walkover-${match.id}`}>
                ⚡ W.O.
              </span>
            )}
            {match.win_type === 'retirement' && (
              <span className="text-xs bg-orange-100 text-orange-900 px-2 py-1 rounded font-bold" data-testid={`badge-retirement-${match.id}`}>
                🤕 פרישה
              </span>
            )}
            {isAdmin && (
              <>
                <button
                  onClick={(e) => {
                    e.stopPropagation();
                    handleMatchClick(match);
                  }}
                  className="text-xs bg-orange-100 text-orange-800 px-2 py-1 rounded hover:bg-orange-200 transition-colors"
                >
                  ✏️ ערוך תוצאה
                </button>
                <button
                  onClick={async (e) => {
                    e.stopPropagation();
                    if (window.confirm('האם לאפס את תוצאת המשחק? הפעולה תמחק את התוצאה ואת ההתקדמות לשלב הבא.')) {
                      try {
                        const token = localStorage.getItem('token') || sessionStorage.getItem('token');
                        await axios.post(
                          `${API}/api/admin/matches/${match.id}/reset-result`,
                          {},
                          { headers: { Authorization: `Bearer ${token}` } }
                        );
                        toast.success('תוצאת המשחק אופסה!');
                        fetchBracket();
                      } catch (err) {
                        toast.error(err.response?.data?.detail || 'שגיאה באיפוס');
                      }
                    }
                  }}
                  className="text-xs bg-red-100 text-red-800 px-2 py-1 rounded hover:bg-red-200 transition-colors"
                >
                  🔄 אפס תוצאה
                </button>
              </>
            )}
          </div>
        )}

        {/* Social: reactions + comments + share — only for completed matches */}
        {match.completed && (
          <div className="mt-2 flex justify-center" onClick={(e) => e.stopPropagation()}>
            <MatchSocial match={match} tournamentName={tournamentName} tournamentDate={tournamentDate} />
          </div>
        )}
        
        {/* משחק לא הושלם עם שחקנים מוגדרים */}
        {!match.completed && user && canSubmitResult && !isPlayersUndefined && (
          <div className="mt-2 flex items-center justify-center gap-2 flex-wrap">
            <span className="text-xs bg-blue-100 text-blue-800 px-2 py-1 rounded">
              👆 לחץ להזנת תוצאה
            </span>
            {isAdmin && (
              <>
                <button
                  onClick={(e) => {
                    e.stopPropagation();
                    handleSetPlayersClick(match);
                  }}
                  className="text-xs bg-purple-100 text-purple-800 px-2 py-1 rounded hover:bg-purple-200 transition-colors"
                >
                  👥 ערוך שחקנים
                </button>
                {/* הסר שחקן - רק בשלבי נוקאאוט */}
                {match.round && !match.group_name && (
                  <div className="flex gap-1">
                    {match.player1_id !== 'TBD' && (
                      <button
                        onClick={async (e) => {
                          e.stopPropagation();
                          if (window.confirm(`להסיר את ${match.player1_name} מהמשחק?`)) {
                            try {
                              const token = localStorage.getItem('token') || sessionStorage.getItem('token');
                              await axios.post(
                                `${API}/api/admin/matches/${match.id}/remove-player-from-match`,
                                { position: 1 },
                                { headers: { Authorization: `Bearer ${token}` } }
                              );
                              toast.success(`${match.player1_name} הוסר`);
                              fetchBracket();
                            } catch (err) {
                              toast.error(err.response?.data?.detail || 'שגיאה');
                            }
                          }
                        }}
                        className="text-xs bg-red-100 text-red-800 px-2 py-1 rounded hover:bg-red-200 transition-colors"
                      >
                        ✖ הסר {match.player1_name?.substring(0, 8)}
                      </button>
                    )}
                    {match.player2_id !== 'TBD' && (
                      <button
                        onClick={async (e) => {
                          e.stopPropagation();
                          if (window.confirm(`להסיר את ${match.player2_name} מהמשחק?`)) {
                            try {
                              const token = localStorage.getItem('token') || sessionStorage.getItem('token');
                              await axios.post(
                                `${API}/api/admin/matches/${match.id}/remove-player-from-match`,
                                { position: 2 },
                                { headers: { Authorization: `Bearer ${token}` } }
                              );
                              toast.success(`${match.player2_name} הוסר`);
                              fetchBracket();
                            } catch (err) {
                              toast.error(err.response?.data?.detail || 'שגיאה');
                            }
                          }
                        }}
                        className="text-xs bg-red-100 text-red-800 px-2 py-1 rounded hover:bg-red-200 transition-colors"
                      >
                        ✖ הסר {match.player2_name?.substring(0, 8)}
                      </button>
                    )}
                  </div>
                )}
              </>
            )}
          </div>
        )}
        
        {/* משחק עם TBD/ממתין - הכפתור תמיד מופיע בנוקאאוט */}
        {!match.completed && isAdmin && isPlayersUndefined && (
          <div className="mt-2 flex items-center justify-center gap-2 flex-wrap">
            <button
              onClick={(e) => {
                e.stopPropagation();
                handleSetPlayersClick(match);
              }}
              className="text-xs bg-green-500 text-white px-3 py-2 rounded-lg hover:bg-green-600 transition-colors font-bold"
            >
              ✏️ הגדר שחקנים למשחק זה
            </button>
            {/* הסרת שחקן בודד שכבר שובץ (כשהצד השני עדיין ממתין) */}
            {!isEmptyMatch && match.round && !match.group_name && (
              <>
                {hasRealPlayer1Name && match.player1_id && match.player1_id !== 'TBD' && (
                  <button
                    data-testid={`remove-player1-${match.id}`}
                    onClick={async (e) => {
                      e.stopPropagation();
                      if (window.confirm(`להסיר את ${match.player1_name} מהמשחק?`)) {
                        try {
                          const token = localStorage.getItem('token') || sessionStorage.getItem('token');
                          await axios.post(
                            `${API}/api/admin/matches/${match.id}/remove-player-from-match`,
                            { position: 1 },
                            { headers: { Authorization: `Bearer ${token}` } }
                          );
                          toast.success(`${match.player1_name} הוסר`);
                          fetchBracket();
                        } catch (err) {
                          toast.error(err.response?.data?.detail || 'שגיאה');
                        }
                      }
                    }}
                    className="text-xs bg-red-100 text-red-800 px-2 py-1 rounded hover:bg-red-200 transition-colors"
                  >
                    ✖ הסר {match.player1_name?.substring(0, 10)}
                  </button>
                )}
                {hasRealPlayer2Name && match.player2_id && match.player2_id !== 'TBD' && (
                  <button
                    data-testid={`remove-player2-${match.id}`}
                    onClick={async (e) => {
                      e.stopPropagation();
                      if (window.confirm(`להסיר את ${match.player2_name} מהמשחק?`)) {
                        try {
                          const token = localStorage.getItem('token') || sessionStorage.getItem('token');
                          await axios.post(
                            `${API}/api/admin/matches/${match.id}/remove-player-from-match`,
                            { position: 2 },
                            { headers: { Authorization: `Bearer ${token}` } }
                          );
                          toast.success(`${match.player2_name} הוסר`);
                          fetchBracket();
                        } catch (err) {
                          toast.error(err.response?.data?.detail || 'שגיאה');
                        }
                      }
                    }}
                    className="text-xs bg-red-100 text-red-800 px-2 py-1 rounded hover:bg-red-200 transition-colors"
                  >
                    ✖ הסר {match.player2_name?.substring(0, 10)}
                  </button>
                )}
              </>
            )}
          </div>
        )}
        
        {!match.completed && !user && (
          <div className="mt-2 text-center">
            <span className="text-xs bg-gray-100 text-gray-600 px-2 py-1 rounded">
              🔐 התחבר להזנת תוצאה
            </span>
          </div>
        )}
      </div>
    );
  };

  if (loading) {
    return (
      <div className="text-center py-12">
        <Clock className="w-12 h-12 mx-auto text-gray-400 animate-spin mb-4" />
        <p className="text-gray-600">טוען ברקט...</p>
      </div>
    );
  }

  // אם אין משחקים, הצג תבנית ריקה
  const hasMatches = bracket && bracket.matches && bracket.matches.length > 0;

  // Group matches by round for display
  const matchesByRound = {};
  
  // בתחרויות נוקאאוט - תמיד הצג את כל השלבים!
  if (tournamentType === 'knockout') {
    // יצירת כל השלבים עם משבצות ריקות
    matchesByRound['round_of_32'] = Array(16).fill(null).map((_, i) => ({
      id: `empty_r32_${i}`,
      round: 'round_of_32',
      position: i + 1,
      player1_id: null,
      player1_name: 'ממתין',
      player2_id: null,
      player2_name: 'ממתין',
      completed: false,
      isEmpty: true
    }));
    matchesByRound['round_of_16'] = Array(8).fill(null).map((_, i) => ({
      id: `empty_r16_${i}`,
      round: 'round_of_16',
      position: i + 1,
      player1_id: null,
      player1_name: 'ממתין',
      player2_id: null,
      player2_name: 'ממתין',
      completed: false,
      isEmpty: true
    }));
    matchesByRound['quarter_final'] = Array(4).fill(null).map((_, i) => ({
      id: `empty_qf_${i}`,
      round: 'quarter_final',
      position: i + 1,
      player1_id: null,
      player1_name: 'ממתין',
      player2_id: null,
      player2_name: 'ממתין',
      completed: false,
      isEmpty: true
    }));
    matchesByRound['semi_final'] = Array(2).fill(null).map((_, i) => ({
      id: `empty_sf_${i}`,
      round: 'semi_final',
      position: i + 1,
      player1_id: null,
      player1_name: 'ממתין',
      player2_id: null,
      player2_name: 'ממתין',
      completed: false,
      isEmpty: true
    }));
    matchesByRound['final'] = [{
      id: 'empty_final',
      round: 'final',
      position: 1,
      player1_id: null,
      player1_name: 'ממתין',
      player2_id: null,
      player2_name: 'ממתין',
      completed: false,
      isEmpty: true
    }];
    matchesByRound['third_place'] = [{
      id: 'empty_third',
      round: 'third_place',
      position: 1,
      player1_id: null,
      player1_name: 'ממתין',
      player2_id: null,
      player2_name: 'ממתין',
      completed: false,
      isEmpty: true
    }];
    
    // טורניר תנחומים - כל השלבים תמיד מוצגים!
    // שמינית גמר תנחומים (8 משחקים)
    matchesByRound['consolation_r16'] = Array(8).fill(null).map((_, i) => ({
      id: `empty_con_r16_${i}`,
      round: 'consolation_r16',
      position: i + 1,
      player1_id: null,
      player1_name: 'ממתין למפסיד',
      player2_id: null,
      player2_name: 'ממתין למפסיד',
      completed: false,
      isEmpty: true
    }));
    // רבע גמר תנחומים (4 משחקים)
    matchesByRound['consolation_quarter'] = Array(4).fill(null).map((_, i) => ({
      id: `empty_con_qf_${i}`,
      round: 'consolation_quarter',
      position: i + 1,
      player1_id: null,
      player1_name: 'ממתין למפסיד',
      player2_id: null,
      player2_name: 'ממתין למפסיד',
      completed: false,
      isEmpty: true
    }));
    // חצי גמר תנחומים (2 משחקים)
    matchesByRound['consolation_semi'] = Array(2).fill(null).map((_, i) => ({
      id: `empty_con_sf_${i}`,
      round: 'consolation_semi',
      position: i + 1,
      player1_id: null,
      player1_name: 'ממתין למפסיד',
      player2_id: null,
      player2_name: 'ממתין למפסיד',
      completed: false,
      isEmpty: true
    }));
    // גמר תנחומים (1 משחק)
    matchesByRound['consolation_final'] = [{
      id: 'empty_con_final',
      round: 'consolation_final',
      position: 1,
      player1_id: null,
      player1_name: 'ממתין למפסיד',
      player2_id: null,
      player2_name: 'ממתין למפסיד',
      completed: false,
      isEmpty: true
    }];
    
    // עכשיו נחליף את המשבצות הריקות במשחקים אמיתיים אם קיימים
    if (hasMatches) {
      // קודם נקבץ את המשחקים לפי סיבוב
      const matchesByRoundFromBracket = {};
      bracket.matches.forEach(match => {
        const roundKey = match.round;
        if (!matchesByRoundFromBracket[roundKey]) {
          matchesByRoundFromBracket[roundKey] = [];
        }
        matchesByRoundFromBracket[roundKey].push(match);
      });
      
      // עכשיו נחליף את המשבצות הריקות
      Object.keys(matchesByRoundFromBracket).forEach(roundKey => {
        const realMatches = matchesByRoundFromBracket[roundKey];
        if (matchesByRound[roundKey]) {
          // יש משבצות ריקות - נחליף אותן במשחקים האמיתיים
          realMatches.forEach((match, idx) => {
            if (idx < matchesByRound[roundKey].length) {
              matchesByRound[roundKey][idx] = match;
            } else {
              matchesByRound[roundKey].push(match);
            }
          });
        } else {
          // סיבוב חדש (כמו תנחומים) - פשוט נוסיף
          matchesByRound[roundKey] = realMatches;
        }
      });
    }
  } else if (hasMatches) {
    // תחרויות יומיות - לוגיקה רגילה
    bracket.matches.forEach(match => {
      let roundKey = match.round;
      
      // For group stage matches (round = group_a, group_b, etc.)
      if (match.group_name) {
        roundKey = `group_${match.group_name}`;
      }
      
      if (!matchesByRound[roundKey]) {
        matchesByRound[roundKey] = [];
      }
      matchesByRound[roundKey].push(match);
    });
  } else {
    // יצירת תבנית ריקה לתחרות יומית
    matchesByRound['group_A'] = [];
    matchesByRound['group_B'] = [];
    matchesByRound['group_C'] = [];
    matchesByRound['group_D'] = [];
    matchesByRound['quarter_final'] = Array(4).fill(null);
    matchesByRound['semi_final'] = Array(2).fill(null);
    matchesByRound['final'] = Array(1).fill(null);
  }

  // חישוב פודיום (זוכים) לטבלת הסיכום ב-PDF — מתוך המשחקים
  const computePodium = () => {
    const ms = bracket?.matches || [];
    const nameFor = (m, id) => (id === m.player1_id ? m.player1_name : id === m.player2_id ? m.player2_name : null);
    const finalM = ms.find((m) => m.round === 'final' && m.completed && m.winner_id);
    if (!finalM) return null;
    const firstName = nameFor(finalM, finalM.winner_id);
    const loserId = finalM.loser_id || (finalM.winner_id === finalM.player1_id ? finalM.player2_id : finalM.player1_id);
    const secondName = nameFor(finalM, loserId);
    const thirdM = ms.find((m) => m.round === 'third_place' && m.completed && m.winner_id);
    const thirdName = thirdM ? nameFor(thirdM, thirdM.winner_id) : null;
    const consoM = ms.find((m) => m.round === 'consolation_final' && m.completed && m.winner_id);
    const consoName = consoM ? nameFor(consoM, consoM.winner_id) : null;
    const prizes = bracket?.tournament?.prizes || {};
    return { firstName, secondName, thirdName, consoName, prizes };
  };
  const pdfPodium = computePodium();

  return (
    <div dir="rtl">
      {/* טבלת סיכום נסתרת לייצוא PDF (נצרבת כתמונה לתמיכה בעברית) */}
      {pdfPodium && (
        <div
          id="pdf-summary-area"
          style={{ position: 'fixed', left: '-99999px', top: 0, width: '760px', background: '#ffffff', direction: 'rtl', fontFamily: 'Arial, sans-serif', padding: '24px', boxSizing: 'border-box' }}
        >
          <div style={{ textAlign: 'center', fontSize: '26px', fontWeight: 800, color: '#0f172a', marginBottom: '4px' }}>
            🏆 {tournamentName || 'התחרות'}
          </div>
          <div style={{ textAlign: 'center', fontSize: '14px', color: '#64748b', marginBottom: '20px' }}>
            {tournamentDate ? new Date(tournamentDate).toLocaleDateString('he-IL', { weekday: 'long', year: 'numeric', month: 'long', day: 'numeric' }) : ''}
          </div>
          <div style={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
            {[
              { medal: '🥇', label: 'מקום ראשון', name: pdfPodium.firstName, prize: pdfPodium.prizes.first_place, bg: '#fef9c3', border: '#eab308' },
              { medal: '🥈', label: 'מקום שני', name: pdfPodium.secondName, prize: pdfPodium.prizes.second_place, bg: '#f1f5f9', border: '#94a3b8' },
              { medal: '🥉', label: 'מקום שלישי', name: pdfPodium.thirdName, prize: pdfPodium.prizes.third_place, bg: '#ffedd5', border: '#f97316' },
              { medal: '🏅', label: 'אלוף התנחומים', name: pdfPodium.consoName, prize: pdfPodium.prizes.consolation_winner, bg: '#dbeafe', border: '#3b82f6' },
            ].filter((r) => r.name).map((r, i) => (
              <div key={i} style={{ display: 'flex', alignItems: 'center', gap: '14px', background: r.bg, border: `2px solid ${r.border}`, borderRadius: '12px', padding: '12px 18px' }}>
                <span style={{ fontSize: '34px' }}>{r.medal}</span>
                <div style={{ flex: 1 }}>
                  <div style={{ fontSize: '13px', color: '#475569', fontWeight: 600 }}>{r.label}</div>
                  <div style={{ fontSize: '22px', fontWeight: 800, color: '#0f172a' }}>{r.name}</div>
                </div>
                {r.prize && (
                  <div style={{ fontSize: '13px', color: '#334155', background: '#ffffff', borderRadius: '8px', padding: '6px 12px', maxWidth: '240px' }}>
                    🎁 {r.prize}
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>
      )}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2 justify-end flex-row-reverse">
            <Trophy className="w-6 h-6" />
            🎾 ברקט התחרות - {tournamentType === 'daily' ? 'תחרות יומית' : 'תחרות נוקאאוט'} 🎾
          </CardTitle>
          {!hasMatches && (
            <p className="text-sm text-gray-500 mt-2 text-right">
              👀 זוהי תצוגה מקדימה של מבנה הברקט. המשחקים יתמלאו לאחר תחילת התחרות.
            </p>
          )}
          
          {/* כפתורי עריכה לאדמין - זמין בכל סוגי התחרויות */}
          <div className="flex flex-wrap gap-2 mt-3">
            {hasMatches && (
              <Button
                onClick={async () => {
                  const el = document.getElementById('bracket-capture-area');
                  if (!el) return;
                  toast.info('מכין תמונה...');
                  try {
                    const res = await shareElementAsImage(el, { filename: 'tennis-bracket.png', title: '🎾 ברקט התחרות - טניס פרו ישראל' });
                    toast.success(res === 'shared' ? 'התמונה שותפה!' : 'התמונה הורדה בהצלחה 📸');
                  } catch (e) {
                    console.error(e);
                    toast.error('שגיאה בהכנת התמונה');
                  }
                }}
                variant="outline"
                size="sm"
                data-testid="share-bracket-image-btn"
                className="border-emerald-500 text-emerald-600 hover:bg-emerald-50 font-bold"
              >
                📸 שתף ברקט כתמונה
              </Button>
            )}
            {hasMatches && (
              <Button
                onClick={async () => {
                  const el = document.getElementById('bracket-capture-area');
                  if (!el) return;
                  toast.info('מכין PDF...');
                  try {
                    const summaryEl = document.getElementById('pdf-summary-area');
                    await exportElementAsPdf(el, {
                      filename: `tennis-bracket-${tournamentName || 'tournament'}.pdf`,
                      title: tournamentName || 'ברקט התחרות',
                      subtitle: tournamentDate ? new Date(tournamentDate).toLocaleDateString('he-IL') : '',
                      summaryElement: pdfPodium ? summaryEl : null,
                    });
                    toast.success('ה-PDF הורד בהצלחה 📄');
                  } catch (e) {
                    console.error(e);
                    toast.error('שגיאה בהכנת ה-PDF');
                  }
                }}
                variant="outline"
                size="sm"
                data-testid="export-bracket-pdf-btn"
                className="border-blue-500 text-blue-600 hover:bg-blue-50 font-bold"
              >
                📄 ייצוא ל-PDF
              </Button>
            )}
            {isAdmin && (
              <>
              <Button
                onClick={() => {
                  if (swapMode) {
                    cancelSwapMode();
                  } else {
                    setSwapMode(true);
                    toast.info('מצב החלפה פעיל', { description: 'לחץ על שחקן ואז על שחקן אחר להחלפה' });
                  }
                }}
                variant={swapMode ? "destructive" : "outline"}
                size="sm"
                className={swapMode ? "bg-red-500 hover:bg-red-600" : "border-purple-500 text-purple-600 hover:bg-purple-50"}
              >
                {swapMode ? '❌ בטל מצב עריכה' : '🔄 הזז / החלף שחקנים'}
              </Button>
              
              {selectedForSwap && (
                <span className="text-sm text-purple-600 flex items-center gap-1 bg-purple-100 px-3 py-1 rounded">
                  ✓ נבחר: <strong>{selectedForSwap.playerName}</strong> — לחץ על משבצת יעד
                </span>
              )}
              </>
            )}
          </div>
        </CardHeader>
        <CardContent>
          <div id="bracket-capture-area">
          {tournamentType === 'daily' ? (
            <DailyTournamentBracket matches={matchesByRound} renderMatch={renderMatch} isEmpty={!hasMatches} tournamentType={tournamentType} isAdmin={isAdmin} openEditGroupsModal={openEditGroupsModal} tournamentId={tournamentId} />
          ) : (
            <KnockoutTournamentBracket matches={matchesByRound} renderMatch={renderMatch} isEmpty={!hasMatches} tournamentType={tournamentType} isAdmin={isAdmin} fetchConsolationWhatsapp={fetchConsolationWhatsapp} consolationWaLinks={consolationWaLinks} />
          )}
          <SponsorBanner className="mt-6 mx-auto max-w-xs" />
          </div>
        </CardContent>
      </Card>

      {/* Result Entry Modal */}
      <Dialog open={isResultModalOpen} onOpenChange={setIsResultModalOpen}>
        <DialogContent className="sm:max-w-[500px]">
          <DialogHeader>
            <DialogTitle>הזנת תוצאה</DialogTitle>
          </DialogHeader>
          
          {selectedMatch && (
            <div className="space-y-4 py-4">
              <div className="text-center mb-4">
                <p className="font-bold">{selectedMatch.player1_name}</p>
                <p className="text-gray-500">vs</p>
                <p className="font-bold">{selectedMatch.player2_name}</p>
              </div>

              {/* Win Type Selector — סוג סיום המשחק */}
              <div className="space-y-2">
                <Label>סוג סיום</Label>
                <div className="grid grid-cols-3 gap-2">
                  {[
                    { value: 'normal', label: '🎾 רגיל', desc: 'תוצאה רגילה' },
                    { value: 'retirement', label: '🤕 פרישה', desc: 'אחד פרש' },
                    { value: 'walkover', label: '⚡ W.O.', desc: 'עלה אוטו׳' },
                  ].map(opt => (
                    <button
                      key={opt.value}
                      type="button"
                      onClick={() => { setWinType(opt.value); setValidationErrors([]); }}
                      data-testid={`win-type-${opt.value}`}
                      className={`p-2 rounded-lg border-2 text-center transition-all ${
                        winType === opt.value
                          ? 'border-emerald-500 bg-emerald-50 shadow-sm'
                          : 'border-slate-200 bg-white hover:border-slate-300'
                      }`}
                    >
                      <div className="text-sm font-bold">{opt.label}</div>
                      <div className="text-[10px] text-slate-500">{opt.desc}</div>
                    </button>
                  ))}
                </div>
                {winType === 'walkover' && (
                  <div className="bg-amber-50 border border-amber-200 rounded-lg p-2 text-xs text-amber-900">
                    ⚡ <strong>W.O. (עלה אוטומטית)</strong> — היריב לא הופיע. בחר מי המנצח. אין צורך בסטים.
                  </div>
                )}
                {winType === 'retirement' && (
                  <div className="bg-orange-50 border border-orange-200 rounded-lg p-2 text-xs text-orange-900">
                    🤕 <strong>פרישה</strong> — שחקן פרש באמצע המשחק. בחר מי המנצח (השחקן שנותר). אם רוצים, אפשר להזין סטים שכבר שוחקו לפני הפרישה.
                  </div>
                )}
              </div>

              {/* Validation Errors */}
              {validationErrors.length > 0 && (
                <div className="bg-red-50 border border-red-200 rounded-lg p-3 space-y-1">
                  <div className="flex items-center gap-2 text-red-800 font-semibold mb-2">
                    <AlertCircle className="w-4 h-4" />
                    <span>שגיאות ולידציה:</span>
                  </div>
                  {validationErrors.map((error, idx) => (
                    <p key={idx} className="text-sm text-red-700">• {error}</p>
                  ))}
                </div>
              )}

              {/* Sets Input — מוסתר עבור W.O. */}
              {winType !== 'walkover' && sets.map((set, index) => (
                <div key={index} className="grid grid-cols-3 gap-4 items-center">
                  <div>
                    <Label>סט {index + 1} - {selectedMatch.player1_name}</Label>
                    <Input
                      type="number"
                      min="0"
                      max={index === 2 ? "10" : "7"}
                      value={set.player1_score}
                      onChange={(e) => {
                        const newSets = [...sets];
                        newSets[index].player1_score = e.target.value;
                        setSets(newSets);
                        setValidationErrors([]); // Clear errors on input
                      }}
                      placeholder="0"
                    />
                  </div>
                  <div className="text-center pt-6">:</div>
                  <div>
                    <Label>סט {index + 1} - {selectedMatch.player2_name}</Label>
                    <Input
                      type="number"
                      min="0"
                      max={index === 2 ? "10" : "7"}
                      value={set.player2_score}
                      onChange={(e) => {
                        const newSets = [...sets];
                        newSets[index].player2_score = e.target.value;
                        setSets(newSets);
                        setValidationErrors([]); // Clear errors on input
                      }}
                      placeholder="0"
                    />
                  </div>
                </div>
              ))}

              {winType !== 'walkover' && (
                <div className="bg-blue-50 border border-blue-200 rounded-lg p-3">
                  <p className="text-xs text-blue-900">
                    💡 <strong>דוגמאות לתוצאות תקינות:</strong>
                    <br />• סט רגיל: 6-4, 6-3, 6-2, 6-1, 6-0
                    <br />• סט צמוד: 7-5, 7-6
                    {tournamentType === 'daily' ? (
                      <>
                        <br />• <strong>תחרות יומית: מערכה אחת בלבד (1 סט)</strong>
                      </>
                    ) : (
                      <>
                        <br />• <strong>תחרות נוקאאוט: 2 סטים מינימום, 3 מקסימום</strong>
                        <br />• <strong>מערכה שלישית: סופר טייברייק (10-8, 11-9, ועד 20-18 לפי הצורך)</strong>
                      </>
                    )}
                  </p>
                </div>
              )}

              {/* Winner Selection */}
              <div className="space-y-2">
                <Label>מנצח המשחק</Label>
                <div className="grid grid-cols-2 gap-2">
                  <Button
                    variant={winnerId === selectedMatch.player1_id ? 'default' : 'outline'}
                    onClick={() => {
                      setWinnerId(selectedMatch.player1_id);
                      setValidationErrors([]);
                    }}
                    className="w-full"
                  >
                    {selectedMatch.player1_name}
                  </Button>
                  <Button
                    variant={winnerId === selectedMatch.player2_id ? 'default' : 'outline'}
                    onClick={() => {
                      setWinnerId(selectedMatch.player2_id);
                      setValidationErrors([]);
                    }}
                    className="w-full"
                  >
                    {selectedMatch.player2_name}
                  </Button>
                </div>
              </div>
            </div>
          )}

          <DialogFooter>
            <Button variant="outline" onClick={() => setIsResultModalOpen(false)}>
              ביטול
            </Button>
            <Button onClick={handleSubmitResult} className="bg-green-600 hover:bg-green-700">
              שמור תוצאה
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Player Assignment Modal */}
      <Dialog open={isPlayerAssignModalOpen} onOpenChange={setIsPlayerAssignModalOpen}>
        <DialogContent className="sm:max-w-[500px] max-h-[80vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>✏️ עריכת שחקנים במשחק</DialogTitle>
            <CardDescription>
              {isDoubles ? 'הזן את שמות שני בני הזוג לכל צד' : 'בחר שחקנים מהרשימה או הזן שם ידנית'}
            </CardDescription>
          </DialogHeader>
          
          {selectedMatchForPlayers && (
            <div className="space-y-4 py-4">
              {/* Round info */}
              <div className="text-center text-sm text-gray-500">
                {selectedMatchForPlayers.round?.includes('quarter') ? 'רבע גמר' : 
                 selectedMatchForPlayers.round?.includes('semi') ? 'חצי גמר' : 
                 selectedMatchForPlayers.round === 'final' ? 'גמר' :
                 selectedMatchForPlayers.round?.includes('third') ? 'מקום שלישי' :
                 selectedMatchForPlayers.round || ''}
              </div>
              
              {/* צד 1 */}
              {(() => {
                const hasExistingPlayer1 = selectedMatchForPlayers.player1_name && 
                  selectedMatchForPlayers.player1_name !== 'ממתין' && 
                  selectedMatchForPlayers.player1_name !== 'TBD' &&
                  selectedMatchForPlayers.player1_id !== 'TBD';
                return (
                <div className={`border rounded-lg p-3 ${hasExistingPlayer1 ? 'bg-blue-100 border-blue-400' : 'bg-blue-50'}`}>
                  <Label className="font-bold text-blue-800 flex items-center gap-2">
                    {isDoubles ? 'זוג 1' : 'שחקן 1'}
                    {hasExistingPlayer1 && <span className="text-xs bg-blue-500 text-white px-2 py-0.5 rounded">קיים</span>}
                  </Label>
                  <Input
                    value={player1Name}
                    onChange={(e) => setPlayer1Name(e.target.value)}
                    placeholder={isDoubles ? "שחקן ראשון בזוג..." : "חפש או הקלד שם שחקן..."}
                    className="mt-2 mb-2"
                  />
                {/* רשימת שחקנים מסוננת */}
                {player1Name && allTournamentUsers.length > 0 && (
                  <div className="max-h-32 overflow-y-auto border rounded bg-white">
                    {allTournamentUsers
                      .filter(u => u.full_name.toLowerCase().includes(player1Name.toLowerCase()))
                      .slice(0, 5)
                      .map(u => (
                        <button
                          key={u.id}
                          onClick={() => setPlayer1Name(u.full_name)}
                          className="w-full text-right px-3 py-2 hover:bg-blue-100 border-b last:border-b-0 flex items-center gap-2"
                        >
                          <Avatar className="w-6 h-6">
                            <AvatarFallback className="bg-blue-600 text-white text-xs">
                              {u.full_name.charAt(0)}
                            </AvatarFallback>
                          </Avatar>
                          <span>{u.full_name}</span>
                          {u.rank && u.rank <= 8 && (
                            <span className="text-xs text-blue-600">(#{u.rank})</span>
                          )}
                        </button>
                      ))
                    }
                  </div>
                )}
                
                {/* שדה שותף לזוגות */}
                {isDoubles && (
                  <>
                    <div className="text-center text-blue-400 text-xs my-2">+ שותף/ה</div>
                    <Input
                      value={partner1Name}
                      onChange={(e) => setPartner1Name(e.target.value)}
                      placeholder="🔍 שחקן שני בזוג..."
                      className="mb-2"
                    />
                    {partner1Name && allTournamentUsers.length > 0 && (
                      <div className="max-h-32 overflow-y-auto border rounded bg-white">
                        {allTournamentUsers
                          .filter(u => u.full_name.toLowerCase().includes(partner1Name.toLowerCase()))
                          .slice(0, 5)
                          .map(u => (
                            <button
                              key={u.id}
                              onClick={() => setPartner1Name(u.full_name)}
                              className="w-full text-right px-3 py-2 hover:bg-blue-100 border-b last:border-b-0 flex items-center gap-2"
                            >
                              <Avatar className="w-6 h-6">
                                <AvatarFallback className="bg-blue-600 text-white text-xs">
                                  {u.full_name.charAt(0)}
                                </AvatarFallback>
                              </Avatar>
                              <span>{u.full_name}</span>
                            </button>
                          ))
                        }
                      </div>
                    )}
                  </>
                )}
              </div>
              );
              })()}
              
              <div className="text-center text-gray-400 font-bold">VS</div>
              
              {/* צד 2 */}
              {(() => {
                const hasExistingPlayer2 = selectedMatchForPlayers.player2_name && 
                  selectedMatchForPlayers.player2_name !== 'ממתין' && 
                  selectedMatchForPlayers.player2_name !== 'TBD' &&
                  selectedMatchForPlayers.player2_id !== 'TBD';
                return (
                <div className={`border rounded-lg p-3 ${hasExistingPlayer2 ? 'bg-green-100 border-green-400' : 'bg-green-50'}`}>
                  <Label className="font-bold text-green-800 flex items-center gap-2">
                    {isDoubles ? 'זוג 2' : 'שחקן 2'}
                    {hasExistingPlayer2 && <span className="text-xs bg-green-500 text-white px-2 py-0.5 rounded">קיים</span>}
                  </Label>
                <Input
                  value={player2Name}
                  onChange={(e) => setPlayer2Name(e.target.value)}
                  placeholder={isDoubles ? "🔍 שחקן ראשון בזוג..." : "🔍 חפש או הקלד שם שחקן..."}
                  className="mt-2 mb-2"
                />
                {/* רשימת שחקנים מסוננת */}
                {player2Name && allTournamentUsers.length > 0 && (
                  <div className="max-h-32 overflow-y-auto border rounded bg-white">
                    {allTournamentUsers
                      .filter(u => u.full_name.toLowerCase().includes(player2Name.toLowerCase()))
                      .slice(0, 5)
                      .map(u => (
                        <button
                          key={u.id}
                          onClick={() => setPlayer2Name(u.full_name)}
                          className="w-full text-right px-3 py-2 hover:bg-green-100 border-b last:border-b-0 flex items-center gap-2"
                        >
                          <Avatar className="w-6 h-6">
                            <AvatarFallback className="bg-green-600 text-white text-xs">
                              {u.full_name.charAt(0)}
                            </AvatarFallback>
                          </Avatar>
                          <span>{u.full_name}</span>
                          {u.rank && u.rank <= 8 && (
                            <span className="text-xs text-green-600">(#{u.rank})</span>
                          )}
                        </button>
                      ))
                    }
                  </div>
                )}
                
                {/* שדה שותף לזוגות */}
                {isDoubles && (
                  <>
                    <div className="text-center text-green-400 text-xs my-2">+ שותף/ה</div>
                    <Input
                      value={partner2Name}
                      onChange={(e) => setPartner2Name(e.target.value)}
                      placeholder="🔍 שחקן שני בזוג..."
                      className="mb-2"
                    />
                    {partner2Name && allTournamentUsers.length > 0 && (
                      <div className="max-h-32 overflow-y-auto border rounded bg-white">
                        {allTournamentUsers
                          .filter(u => u.full_name.toLowerCase().includes(partner2Name.toLowerCase()))
                          .slice(0, 5)
                          .map(u => (
                            <button
                              key={u.id}
                              onClick={() => setPartner2Name(u.full_name)}
                              className="w-full text-right px-3 py-2 hover:bg-green-100 border-b last:border-b-0 flex items-center gap-2"
                            >
                              <Avatar className="w-6 h-6">
                                <AvatarFallback className="bg-green-600 text-white text-xs">
                                  {u.full_name.charAt(0)}
                                </AvatarFallback>
                              </Avatar>
                              <span>{u.full_name}</span>
                            </button>
                          ))
                        }
                      </div>
                    )}
                  </>
                )}
              </div>
              );
              })()}
              
              <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-3">
                <p className="text-xs text-yellow-900">
                  <strong>טיפ:</strong> {isDoubles 
                    ? 'הזן את שמות שני השחקנים בכל זוג. השמות יוצגו בפורמט "שחקן1 / שחקן2"'
                    : 'הקלד שם שחקן חיצוני או בחר מהרשימה. אפשר למלא רק שחקן אחד.'
                  }
                </p>
              </div>
            </div>
          )}

          <DialogFooter>
            <Button variant="outline" onClick={() => setIsPlayerAssignModalOpen(false)}>
              ביטול
            </Button>
            <Button onClick={handleSavePlayers} className="bg-blue-600 hover:bg-blue-700">
              💾 שמור שינויים
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Modal עריכת בתים - לתחרות יומית */}
      <Dialog open={isEditGroupsModalOpen} onOpenChange={setIsEditGroupsModalOpen}>
        <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="text-xl font-bold text-orange-600">
              ✏️ עריכת בתים
            </DialogTitle>
          </DialogHeader>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 pt-4">
            {Object.entries(tournamentGroups).sort().map(([groupName, players]) => {
              const groupPlayerIds = players || [];
              
              return (
                <div key={groupName} className="border-2 border-blue-300 rounded-lg p-4 bg-blue-50">
                  <h3 className="text-lg font-bold text-center mb-3 bg-blue-600 text-white py-2 rounded">
                    בית {groupName}
                  </h3>
                  
                  {/* רשימת שחקנים בבית */}
                  <div className="space-y-2 mb-4">
                    {groupPlayerIds.map((playerId, idx) => {
                      const player = allTournamentUsers.find(u => u.id === playerId);
                      const playerName = player?.full_name || playerId;
                      
                      return (
                        <div key={playerId} className="flex items-center justify-between bg-white p-2 rounded border">
                          <span className="font-medium">
                            {idx + 1}. {playerName}
                          </span>
                          <div className="flex gap-1">
                            {/* העבר לבית אחר */}
                            <select
                              className="text-xs border rounded px-1 py-1"
                              onChange={(e) => {
                                if (e.target.value) {
                                  movePlayerToGroup(playerId, groupName, e.target.value);
                                  e.target.value = '';
                                }
                              }}
                              defaultValue=""
                            >
                              <option value="">העבר ל...</option>
                              {Object.keys(tournamentGroups)
                                .filter(g => g !== groupName)
                                .sort()
                                .map(g => (
                                  <option key={g} value={g}>בית {g}</option>
                                ))
                              }
                            </select>
                            {/* הסר */}
                            <button
                              onClick={() => {
                                if (window.confirm(`להסיר את ${playerName} מבית ${groupName}?`)) {
                                  removePlayerFromGroup(playerId, groupName);
                                }
                              }}
                              className="text-red-500 hover:bg-red-100 px-2 py-1 rounded text-sm"
                            >
                              ❌
                            </button>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                  
                  {/* הוסף שחקן */}
                  <div className="border-t pt-3">
                    <div className="flex gap-2">
                      <select
                        id={`add-to-group-${groupName}`}
                        className="flex-1 text-sm border rounded px-2 py-2"
                        defaultValue=""
                        data-testid={`add-to-group-${groupName}-select`}
                        onChange={(e) => {
                          if (e.target.value) {
                            addPlayerToGroup(e.target.value, groupName);
                            e.target.value = '';
                          }
                        }}
                      >
                        <option value="">➕ הוסף שחקן לבית {groupName}...</option>
                        {(() => {
                          const allInGroups = Object.values(tournamentGroups).flat().map(p => typeof p === 'object' ? p.id : p);
                          const isAvailable = (uid) => !allInGroups.includes(uid);
                          // 1. רשימת המתנה - בעדיפות עליונה
                          const waitingUsers = allTournamentUsers.filter(u => tournamentWaitingList.includes(u.id) && isAvailable(u.id));
                          // 2. שחקנים רשומים אך לא משובצים לבית
                          const registeredButNotInGroup = allTournamentUsers.filter(u =>
                            tournamentRegistered.includes(u.id) && !tournamentWaitingList.includes(u.id) && isAvailable(u.id)
                          );
                          // 3. שאר המשתמשים באתר
                          const others = allTournamentUsers.filter(u =>
                            !tournamentRegistered.includes(u.id) && !tournamentWaitingList.includes(u.id) && isAvailable(u.id)
                          );
                          return (
                            <>
                              {waitingUsers.length > 0 && (
                                <optgroup label={`⏳ רשימת המתנה (${waitingUsers.length})`}>
                                  {waitingUsers.map(u => (
                                    <option key={u.id} value={u.id}>⏳ {u.full_name}</option>
                                  ))}
                                </optgroup>
                              )}
                              {registeredButNotInGroup.length > 0 && (
                                <optgroup label="✅ רשומים לתחרות">
                                  {registeredButNotInGroup.map(u => (
                                    <option key={u.id} value={u.id}>{u.full_name}</option>
                                  ))}
                                </optgroup>
                              )}
                              {others.length > 0 && (
                                <optgroup label="👥 שאר המשתמשים">
                                  {others.map(u => (
                                    <option key={u.id} value={u.id}>{u.full_name}</option>
                                  ))}
                                </optgroup>
                              )}
                            </>
                          );
                        })()}
                      </select>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
          <div className="flex justify-end pt-4">
            <Button variant="outline" onClick={() => setIsEditGroupsModalOpen(false)}>
              סגור
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};

// Daily Tournament Bracket (4 groups + playoffs)
const DailyTournamentBracket = ({ matches, renderMatch, isEmpty, tournamentType, isAdmin, openEditGroupsModal, tournamentId }) => {
  // מצא את הבתים שבאמת קיימים במשחקים
  const allGroups = ['group_A', 'group_B', 'group_C', 'group_D'];
  const existingGroups = allGroups.filter(group => matches[group] && matches[group].length > 0);
  
  // אם אין בתים קיימים, הצג את כולם (ריק)
  const groups = existingGroups.length > 0 ? existingGroups : allGroups;
  
  const playoffs = ['quarter_final', 'semi_final', 'final'];
  
  // Extract unique players from group matches
  const getGroupPlayers = (groupMatches) => {
    if (!groupMatches || groupMatches.length === 0) return [];
    
    const playerSet = new Set();
    groupMatches.forEach(match => {
      if (match.player1_name) playerSet.add(match.player1_name);
      if (match.player2_name) playerSet.add(match.player2_name);
    });
    
    return Array.from(playerSet);
  };
  
  // Compute standings (wins/losses + games won/lost) per player from group matches
  const getGroupStandings = (groupMatches) => {
    const stats = {};
    const ensure = (name) => {
      if (!name) return;
      if (!stats[name]) stats[name] = { name, wins: 0, losses: 0, games_won: 0, games_lost: 0 };
    };
    (groupMatches || []).forEach(match => {
      ensure(match.player1_name);
      ensure(match.player2_name);
      if (match.completed && match.winner_id) {
        // Match wins/losses
        if (match.winner_id === match.player1_id) {
          if (match.player1_name) stats[match.player1_name].wins += 1;
          if (match.player2_name) stats[match.player2_name].losses += 1;
        } else if (match.winner_id === match.player2_id) {
          if (match.player2_name) stats[match.player2_name].wins += 1;
          if (match.player1_name) stats[match.player1_name].losses += 1;
        }
        // Games (משחקונים) won/lost per set
        if (match.sets && match.sets.length > 0) {
          match.sets.forEach(set => {
            const p1g = set.player1_games ?? set.player1_score ?? 0;
            const p2g = set.player2_games ?? set.player2_score ?? 0;
            if (match.player1_name) {
              stats[match.player1_name].games_won += p1g;
              stats[match.player1_name].games_lost += p2g;
            }
            if (match.player2_name) {
              stats[match.player2_name].games_won += p2g;
              stats[match.player2_name].games_lost += p1g;
            }
          });
        }
      }
    });
    return Object.values(stats).sort((a, b) => 
      b.wins - a.wins || 
      a.losses - b.losses ||
      (b.games_won - b.games_lost) - (a.games_won - a.games_lost) ||
      b.games_won - a.games_won
    );
  };
  
  // מיפוי אותיות בתים לעברית
  const groupHebrewNames = {
    'group_A': 'א',
    'group_B': 'ב',
    'group_C': 'ג',
    'group_D': 'ד'
  };
  
  return (
    <div className="space-y-8" dir="rtl">
      {/* Group Stage */}
      <div>
        <div className="text-center mb-4 p-4 bg-gradient-to-r from-emerald-50 to-green-100 rounded-xl border-2 border-emerald-300 shadow-sm">
          <h3 className="text-2xl font-bold mb-2 text-emerald-900">🎾 שלב בתים</h3>
          <p className="text-lg font-semibold text-emerald-800">
            ⭐ שחקנים שלא עולים לפלייאוף: 15 נקודות
          </p>
          {/* כפתור ערוך בתים לאדמין */}
          {isAdmin && groups.length > 0 && (
            <Button 
              onClick={openEditGroupsModal}
              className="mt-3 bg-orange-500 hover:bg-orange-600 text-white font-bold"
            >
              ✏️ ערוך בתים
            </Button>
          )}
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
          {groups.map((group, idx) => {
            const groupMatches = matches[group] || [];
            const groupPlayers = getGroupPlayers(groupMatches);
            const standings = getGroupStandings(groupMatches);
            const hebrewName = groupHebrewNames[group] || (idx + 1);
            // מספר המשחקים בבית לפי מספר השחקנים (Round Robin): n*(n-1)/2
            // 3 שחקנים = 3 משחקים, 4 שחקנים = 6 משחקים וכו'
            const numInGroup = groupPlayers.length;
            const expectedMatches = numInGroup >= 2
              ? (numInGroup * (numInGroup - 1)) / 2
              : Math.max(1, groupMatches.length);
            const completedCount = groupMatches.filter(m => m && m.completed).length;
            const progressPct = expectedMatches > 0 ? Math.min(100, (completedCount / expectedMatches) * 100) : 0;
            const isGroupComplete = expectedMatches > 0 && completedCount >= expectedMatches && numInGroup >= 2;
            
            return (
              <div key={group} className="w-full bg-white rounded-xl overflow-hidden shadow-lg border-2 border-emerald-200 hover:shadow-emerald-100 hover:shadow-xl transition-shadow">
                {/* Scorecard Header */}
                <div className="bg-gradient-to-r from-emerald-600 to-green-700 px-3 py-2.5 flex items-center justify-between">
                  <div className="flex items-center gap-2">
                    <div className="w-10 h-10 bg-white rounded-full flex items-center justify-center font-black text-emerald-700 text-lg shadow-lg">
                      {hebrewName}
                    </div>
                    <div>
                      <p className="text-white text-base font-bold leading-none">בית {hebrewName}</p>
                      <p className="text-emerald-100 text-[10px] mt-0.5">Round Robin</p>
                    </div>
                  </div>
                  <div className="text-left">
                    {isGroupComplete ? (
                      <span data-testid={`group-complete-${group}`} className="inline-flex items-center gap-1 bg-white text-emerald-700 text-[11px] font-black px-2 py-1 rounded-full shadow">
                        ✓ הושלם
                      </span>
                    ) : (
                      <>
                        <p className="text-emerald-100 text-[10px] font-bold">{completedCount}/{expectedMatches}</p>
                        <div className="w-16 h-1.5 bg-emerald-900/40 rounded-full mt-1 overflow-hidden">
                          <div className="h-full bg-white rounded-full transition-all" style={{ width: `${progressPct}%` }} />
                        </div>
                      </>
                    )}
                  </div>
                </div>
                
                {/* Standings Table */}
                {standings.length > 0 ? (
                  <div className="bg-emerald-50/40 px-2 py-2 border-b border-emerald-100">
                    <table className="w-full text-[11px] sm:text-xs">
                      <thead>
                        <tr className="text-emerald-700">
                          <th className="text-right py-1 w-5" title="מיקום">#</th>
                          <th className="text-right py-1">שחקן</th>
                          <th className="text-center py-1 w-7" title="ניצחונות במשחקים">נצ׳</th>
                          <th className="text-center py-1 w-7" title="הפסדים במשחקים">הפ׳</th>
                          <th className="text-center py-1 w-7" title="משחקונים שניצח (games won)">+</th>
                          <th className="text-center py-1 w-7" title="משחקונים שהפסיד (games lost)">−</th>
                        </tr>
                      </thead>
                      <tbody>
                        {standings.map((player, pIdx) => {
                          const diff = player.games_won - player.games_lost;
                          return (
                            <tr key={pIdx} className="border-t border-emerald-100">
                              <td className="py-1.5">
                                <div className={`w-5 h-5 ${pIdx < 2 ? 'bg-gradient-to-br from-emerald-500 to-green-600' : 'bg-gray-400'} text-white text-[10px] rounded font-bold flex items-center justify-center`}>
                                  {pIdx + 1}
                                </div>
                              </td>
                              <td className="py-1.5 font-semibold text-slate-700 truncate max-w-[90px]" title={`${player.name} · מאזן משחקונים: ${diff > 0 ? '+' : ''}${diff}`}>{player.name}</td>
                              <td className="py-1.5 text-center">
                                <span className="px-1 py-0.5 bg-emerald-100 text-emerald-700 rounded font-bold text-[10px]">{player.wins}</span>
                              </td>
                              <td className="py-1.5 text-center">
                                <span className="px-1 py-0.5 bg-red-50 text-red-600 rounded font-bold text-[10px]">{player.losses}</span>
                              </td>
                              <td className="py-1.5 text-center">
                                <span className="px-1 py-0.5 bg-blue-50 text-blue-700 rounded font-bold text-[10px]">{player.games_won}</span>
                              </td>
                              <td className="py-1.5 text-center">
                                <span className="px-1 py-0.5 bg-orange-50 text-orange-600 rounded font-bold text-[10px]">{player.games_lost}</span>
                              </td>
                            </tr>
                          );
                        })}
                      </tbody>
                    </table>
                  </div>
                ) : groupPlayers.length > 0 ? (
                  <div className="bg-emerald-50/40 px-3 py-2 border-b border-emerald-100">
                    <ul className="text-xs sm:text-sm space-y-1" dir="rtl">
                      {groupPlayers.map((player, pIdx) => (
                        <li key={pIdx} className="flex items-center gap-2">
                          <span className="w-5 h-5 bg-emerald-500 text-white rounded font-bold flex items-center justify-center text-[10px] flex-shrink-0">
                            {pIdx + 1}
                          </span>
                          <span className="text-slate-700 truncate font-semibold">{player}</span>
                        </li>
                      ))}
                    </ul>
                  </div>
                ) : null}
                
                {/* Matches */}
                <div className="space-y-2 bg-white p-2 sm:p-3">
                  {groupMatches.length > 0 ? (
                    <>
                      <p className="text-[10px] sm:text-xs font-bold text-slate-500 mb-2 text-right tracking-wider">משחקים</p>
                      {groupMatches.map((match, matchIdx) => (
                        <div key={match?.id || `empty-${matchIdx}`}>{renderMatch(match)}</div>
                      ))}
                    </>
                  ) : (
                    <div className="text-center py-6 sm:py-8">
                      <div className="text-3xl sm:text-4xl font-bold text-emerald-200 mb-2">{idx + 1}</div>
                      <p className="text-xs text-slate-500">4 שחקנים</p>
                      <p className="text-xs text-slate-500">משחק כל אחד נגד כל אחד</p>
                    </div>
                  )}
                </div>
              </div>
            );
          })}
        </div>
        
        {isEmpty && (
          <div className="mt-4 p-4 bg-blue-50 border border-blue-200 rounded-lg">
            <p className="text-sm text-blue-900">
              💡 <strong>איך זה עובד?</strong> כל בית מכיל 4 שחקנים. בכל בית כל שחקן משחק נגד כל שחקן (6 משחקים לבית).
              2 השחקנים הראשונים מכל בית עולים לשלב רבע הגמר.
            </p>
          </div>
        )}
      </div>

      {/* Crossover Explanation */}
      {isEmpty && (
        <div className="bg-gradient-to-r from-orange-100 to-yellow-100 p-6 rounded-lg border-2 border-orange-300">
          <h4 className="text-lg font-bold mb-3 text-center">🔀 הצלבות לרבע גמר</h4>
          <p className="text-xs text-center text-orange-700 mb-3">
            ההצלבות מסודרות כך שמקום 1 ו-2 מאותו בית יכולים להיפגש רק בגמר
          </p>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="bg-white p-3 rounded shadow">
              <p className="font-bold text-orange-600">משחק 1 (עליון):</p>
              <p className="text-sm">בית 1 מקום ראשון 🆚 בית 4 מקום שני</p>
            </div>
            <div className="bg-white p-3 rounded shadow">
              <p className="font-bold text-orange-600">משחק 2:</p>
              <p className="text-sm">בית 3 מקום ראשון 🆚 בית 2 מקום שני</p>
            </div>
            <div className="bg-white p-3 rounded shadow">
              <p className="font-bold text-orange-600">משחק 3:</p>
              <p className="text-sm">בית 2 מקום ראשון 🆚 בית 3 מקום שני</p>
            </div>
            <div className="bg-white p-3 rounded shadow">
              <p className="font-bold text-orange-600">משחק 4 (תחתון):</p>
              <p className="text-sm">בית 4 מקום ראשון 🆚 בית 1 מקום שני</p>
            </div>
          </div>
        </div>
      )}

      {/* Playoffs */}
      <div>
        <h3 className="text-xl font-bold mb-4">שלבי הפלייאוף</h3>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          {playoffs.filter(r => (matches[r] || []).length > 0).map(round => {
            const roundNames = {
              'quarter_final': 'רבע גמר',
              'semi_final': 'חצי גמר',
              'final': 'גמר'
            };
            
            // נקודות לכל שלב - תחרויות יומיות
            const dailyPoints = {
              'quarter_final': 35,  // רבע גמר
              'semi_final': 65,     // חצי גמר
              'final_loser': 90,    // מקום 2
              'final_winner': 150   // מקום 1
            };
            
            // הצגת המשחקים שקיימים בפועל (ללא ריפוד מלאכותי - המבנה נקבע בשרת לפי מספר הבתים)
            const roundMatches = matches[round] || [];
            const displayMatches = [...roundMatches];
            
            return (
              <div key={round}>
                <div className="text-center bg-gradient-to-r from-blue-500 to-blue-600 text-white py-3 rounded-t-xl mb-2 shadow-md shadow-blue-200">
                  <h4 className="text-lg font-bold">
                    {roundNames[round]}
                  </h4>
                  {round === 'final' ? (
                    <div className="mt-2 space-y-1">
                      <p className="text-sm font-bold">
                        🥇 מנצח: ⭐ 150 נקודות
                      </p>
                      <p className="text-sm font-bold">
                        🥈 מפסיד: ⭐ 90 נקודות
                      </p>
                    </div>
                  ) : (
                    <p className="text-sm font-semibold mt-1">
                      ⭐ {dailyPoints[round] || '?'} נקודות
                    </p>
                  )}
                  {isAdmin && (
                    <p className="text-xs mt-1 opacity-80">
                      לחץ על משחק לעריכת שחקנים/תוצאות
                    </p>
                  )}
                </div>
                <div className="space-y-2 bg-gray-50 p-3 rounded-b-lg">
                  {displayMatches.map((match, idx) => (
                    <div key={match?.id || `empty-${idx}`}>{renderMatch(match)}</div>
                  ))}
                </div>
              </div>
            );
          })}
        </div>
        
        {/* Winner Display - If final is completed */}
        {(() => {
          const finalMatch = matches['final']?.[0];
          const winner = finalMatch?.completed && finalMatch?.winner_id 
            ? {
                id: finalMatch.winner_id,
                name: finalMatch.winner_id === finalMatch.player1_id ? finalMatch.player1_name : finalMatch.player2_name
              }
            : null;
          
          return winner ? (
            <div className="mt-6 bg-gradient-to-br from-yellow-400 via-yellow-500 to-yellow-600 p-8 rounded-2xl shadow-2xl border-4 border-yellow-300 text-center">
              <Trophy className="w-20 h-20 text-white mx-auto mb-4 animate-bounce" />
              <div className="bg-white/30 backdrop-blur-sm rounded-xl p-4 mb-3">
                <h3 className="text-3xl font-black text-white mb-2 drop-shadow-lg">
                  🏆 מנצח התחרות 🏆
                </h3>
                <a 
                  href={`/profile/${winner.id}`}
                  className="text-4xl font-extrabold text-white hover:text-yellow-100 transition-colors block"
                >
                  {winner.name}
                </a>
              </div>
              <div className="text-lg text-white/90 font-medium">
                🎁 זוכה בפרס הראשון
              </div>
            </div>
          ) : null;
        })()}
      </div>
    </div>
  );
};

// Knockout Tournament Bracket (32 players)
const KnockoutTournamentBracket = ({ matches, renderMatch, isEmpty, tournamentType, isAdmin, fetchConsolationWhatsapp, consolationWaLinks }) => {
  const rounds = ['round_of_32', 'round_of_16', 'quarter_final', 'semi_final', 'final'];
  const roundNames = {
    'round_of_32': 'סיבוב 32',
    'round_of_16': 'שמינית גמר',
    'quarter_final': 'רבע גמר',
    'semi_final': 'חצי גמר',
    'final': 'גמר',
    'third_place': '🥉 משחק מקום 3-4'
  };
  
  // נקודות לכל שלב - נוקאאוט
  const knockoutPoints = {
    'round_of_32': 25,      // סיבוב ראשון
    'round_of_16': 50,      // שמינית גמר
    'quarter_final': 80,    // רבע גמר
    'semi_final': 120,      // מקום 4 (מפסידי חצי)
    'final_loser': 250,     // מקום 2
    'final_winner': 350,    // מקום 1
    'third_place': 170      // מקום 3
  };
  
  // מצא את המנצח אם יש
  const finalMatch = matches['final']?.[0];
  const winner = finalMatch?.completed && finalMatch?.winner_id 
    ? {
        id: finalMatch.winner_id,
        name: finalMatch.winner_id === finalMatch.player1_id ? finalMatch.player1_name : finalMatch.player2_name
      }
    : null;
  
  return (
    <div className="overflow-x-auto -mx-4 px-4 pb-4">
      <div className="flex gap-4 md:gap-6 min-w-max p-2 md:p-4">
        {rounds.map((round, roundIdx) => (
          <div key={round} className="flex-1 min-w-[200px] md:min-w-[250px]">
            <div className={`text-center sticky top-0 ${
              round === 'final'
                ? 'bg-gradient-to-r from-blue-600 to-blue-700 ring-2 ring-blue-300 ring-offset-2'
                : 'bg-gradient-to-r from-blue-500 to-blue-600'
            } text-white py-3 rounded-xl shadow-lg shadow-blue-200 z-10 mb-4`}>
              <h4 className="text-lg font-bold flex items-center justify-center gap-2">
                {round === 'final' && <Trophy className="w-5 h-5" />}
                {roundNames[round]}
              </h4>
              {round === 'final' && tournamentType === 'knockout' ? (
                <div className="mt-2 space-y-1">
                  <p className="text-sm font-bold">
                    🥇 מנצח: ⭐ 350 נקודות
                  </p>
                  <p className="text-sm font-bold">
                    🥈 מפסיד: ⭐ 250 נקודות
                  </p>
                </div>
              ) : round === 'final' && tournamentType === 'daily' ? (
                <div className="mt-2 space-y-1">
                  <p className="text-sm font-bold">
                    🥇 מנצח: ⭐ 150 נקודות
                  </p>
                  <p className="text-sm font-bold">
                    🥈 מפסיד: ⭐ 90 נקודות
                  </p>
                </div>
              ) : (
                <p className="text-sm font-semibold mt-1">
                  ⭐ {knockoutPoints[round] || '?'} נקודות
                </p>
              )}
            </div>
            <div className="space-y-4">
              {matches[round]?.map((match, idx) => (
                <div key={match?.id || `empty-${roundIdx}-${idx}`} className="relative">
                  {renderMatch(match)}
                  {/* Connector lines for visual effect */}
                  {roundIdx < rounds.length - 1 && (
                    <div className="absolute right-0 top-1/2 w-6 h-0.5 bg-emerald-300 -mr-6 hidden lg:block"></div>
                  )}
                </div>
              ))}
              {isEmpty && (
                <p className="text-xs text-center text-gray-500 mt-2 bg-gray-50 p-2 rounded">
                  {matches[round]?.length} משחקים בשלב זה
                </p>
              )}
            </div>
          </div>
        ))}
        
        {/* Winner Display - בנוסף לגמר */}
        {winner && (
          <div className="flex-1 min-w-[300px] flex items-center justify-center">
            <div className="bg-gradient-to-br from-yellow-400 via-yellow-500 to-yellow-600 p-8 rounded-2xl shadow-2xl border-4 border-yellow-300 transform hover:scale-105 transition-transform">
              <div className="text-center">
                <Trophy className="w-20 h-20 text-white mx-auto mb-4 animate-bounce" />
                <div className="bg-white/30 backdrop-blur-sm rounded-xl p-4 mb-3">
                  <h3 className="text-2xl font-black text-white mb-2 drop-shadow-lg">
                    🏆 מנצח 🏆
                  </h3>
                  <a 
                    href={`/profile/${winner.id}`}
                    className="text-3xl font-extrabold text-white hover:text-yellow-100 transition-colors block"
                  >
                    {winner.name}
                  </a>
                </div>
                <div className="text-sm text-white/90 font-medium">
                  🎁 זוכה בפרס הראשון
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
      
      {/* Third Place Match - מתחת לברקט */}
      {matches['third_place'] && matches['third_place'].length > 0 && (
        <div className="mt-8 p-6 bg-gradient-to-r from-orange-50 to-orange-100 rounded-xl border-2 border-orange-300">
          <div className="text-center mb-4">
            <h3 className="text-2xl font-bold text-orange-800 flex items-center justify-center gap-2">
              <span className="text-3xl">🥉</span>
              משחק על מקום 3-4
              <span className="text-3xl">🥉</span>
            </h3>
            <div className="mt-2 flex justify-center gap-4">
              <span className="text-lg font-bold text-green-700 bg-green-100 px-4 py-1 rounded-full">
                מנצח: ⭐ 170 נקודות (מקום 3)
              </span>
              <span className="text-lg font-bold text-blue-700 bg-blue-100 px-4 py-1 rounded-full">
                מפסיד: ⭐ 120 נקודות (מקום 4)
              </span>
            </div>
          </div>
          <div className="max-w-2xl mx-auto">
            {matches['third_place'].map((match) => (
              <div key={match?.id || 'third-place'}>
                {renderMatch(match)}
              </div>
            ))}
          </div>
          <p className="text-center text-sm text-orange-700 mt-4 font-medium">
            מפסידי חצי הגמר מתמודדים על מקום 3 והמדליה הארד
          </p>
        </div>
      )}
      
      {/* === טורניר התנחומים (Consolation Bracket) — מודגש === */}
      {tournamentType === 'knockout' && (
        <div className="mt-10" data-testid="consolation-bracket-section">
          {/* מפריד מודגש */}
          <div className="flex items-center gap-3 mb-4">
            <div className="flex-1 h-1 rounded-full bg-gradient-to-r from-transparent via-amber-400 to-amber-500" />
            <span className="text-xs font-extrabold tracking-wider text-amber-700 bg-amber-100 px-3 py-1 rounded-full border border-amber-300 whitespace-nowrap">מסלול מיוחד</span>
            <div className="flex-1 h-1 rounded-full bg-gradient-to-l from-transparent via-amber-400 to-amber-500" />
          </div>
          <div className="relative p-6 bg-gradient-to-br from-amber-50 via-orange-50 to-teal-50 rounded-2xl border-2 border-amber-300 shadow-xl ring-1 ring-amber-200 overflow-hidden">
            {/* פס עליון צבעוני */}
            <div className="absolute top-0 inset-x-0 h-2 bg-gradient-to-r from-amber-400 via-orange-400 to-teal-400" />
            <div className="text-center mb-6 pt-1">
              <h3 className="text-3xl font-extrabold text-amber-800 flex items-center justify-center gap-2 drop-shadow-sm">
                <span className="text-4xl">🏆</span>
                טורניר התנחומים
                <span className="text-4xl">🏆</span>
              </h3>
              <p className="text-sm text-amber-700/80 mt-2 font-medium">
                מפסידי הסיבוב הראשון מתמודדים על הפרס המיוחד — לכל שחקן יש הזדמנות שנייה!
              </p>
              <div className="mt-3">
                <span className="inline-block text-base font-extrabold text-white bg-gradient-to-r from-amber-500 to-orange-500 px-5 py-1.5 rounded-full shadow animate-pulse">
                  מנצח בתנחומים: ⭐ 50 נקודות בונוס
                </span>
              </div>
            {isAdmin && (
              <div className="mt-4" data-testid="consolation-whatsapp-section">
                <button
                  onClick={fetchConsolationWhatsapp}
                  data-testid="consolation-whatsapp-btn"
                  className="inline-flex items-center gap-2 bg-green-600 hover:bg-green-700 text-white text-sm font-bold px-4 py-2 rounded-full transition-colors shadow-sm"
                >
                  <span className="text-lg">📲</span>
                  שלח WhatsApp למשתתפי התנחומים
                </button>
                {consolationWaLinks && consolationWaLinks.length > 0 && (
                  <div className="mt-3 max-w-xl mx-auto bg-white rounded-xl border border-green-200 p-3 text-right" data-testid="consolation-whatsapp-list">
                    <p className="text-xs text-gray-500 mb-2">לחץ על כל שחקן כדי לפתוח שיחת WhatsApp עם הודעה מוכנה:</p>
                    <div className="space-y-1 max-h-60 overflow-y-auto">
                      {consolationWaLinks.map((t) => (
                        <a
                          key={t.user_id}
                          href={t.whatsapp_url}
                          target="_blank"
                          rel="noopener noreferrer"
                          data-testid={`consolation-wa-link-${t.user_id}`}
                          className="flex items-center justify-between bg-green-50 hover:bg-green-100 rounded-lg px-3 py-2 transition-colors"
                        >
                          <span className="text-green-700 font-bold text-sm">📲 שלח</span>
                          <span className="text-slate-800 text-sm font-semibold">{t.name}</span>
                        </a>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            )}
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
            {/* שמינית גמר תנחומים */}
            {matches['consolation_r16'] && matches['consolation_r16'].length > 0 && (
              <div>
                <div className="text-center bg-gradient-to-r from-teal-400 to-cyan-400 text-white py-2 rounded-t-lg mb-2">
                  <h4 className="text-sm font-bold">שמינית גמר תנחומים</h4>
                </div>
                <div className="space-y-2 bg-white p-2 rounded-b-lg shadow max-h-96 overflow-y-auto">
                  {matches['consolation_r16'].map((match, idx) => (
                    <div key={match?.id || `cr16-${idx}`}>
                      {renderMatch(match)}
                    </div>
                  ))}
                </div>
              </div>
            )}
            
            {/* רבע גמר תנחומים */}
            {matches['consolation_quarter'] && matches['consolation_quarter'].length > 0 && (
              <div>
                <div className="text-center bg-gradient-to-r from-teal-500 to-cyan-500 text-white py-2 rounded-t-lg mb-2">
                  <h4 className="text-sm font-bold">רבע גמר תנחומים</h4>
                </div>
                <div className="space-y-2 bg-white p-2 rounded-b-lg shadow">
                  {matches['consolation_quarter'].map((match, idx) => (
                    <div key={match?.id || `cq-${idx}`}>
                      {renderMatch(match)}
                    </div>
                  ))}
                </div>
              </div>
            )}
            
            {/* חצי גמר תנחומים */}
            {matches['consolation_semi'] && matches['consolation_semi'].length > 0 && (
              <div>
                <div className="text-center bg-gradient-to-r from-teal-500 to-cyan-500 text-white py-2 rounded-t-lg mb-2">
                  <h4 className="text-sm font-bold">חצי גמר תנחומים</h4>
                </div>
                <div className="space-y-2 bg-white p-2 rounded-b-lg shadow">
                  {matches['consolation_semi'].map((match, idx) => (
                    <div key={match?.id || `cs-${idx}`}>
                      {renderMatch(match)}
                    </div>
                  ))}
                </div>
              </div>
            )}
            
            {/* גמר תנחומים */}
            {matches['consolation_final'] && matches['consolation_final'].length > 0 && (
              <div>
                <div className="text-center bg-gradient-to-r from-yellow-500 to-orange-500 text-white py-2 rounded-t-lg mb-2">
                  <h4 className="text-sm font-bold">🏆 גמר תנחומים 🏆</h4>
                </div>
                <div className="space-y-2 bg-white p-2 rounded-b-lg shadow">
                  {matches['consolation_final'].map((match, idx) => (
                    <div key={match?.id || `cf-${idx}`}>
                      {renderMatch(match)}
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
          
          {/* מנצח בתנחומים */}
          {(() => {
            const consolationFinalMatch = matches['consolation_final']?.[0];
            const consolationWinner = consolationFinalMatch?.completed && consolationFinalMatch?.winner_id 
              ? {
                  id: consolationFinalMatch.winner_id,
                  name: consolationFinalMatch.winner_id === consolationFinalMatch.player1_id 
                    ? consolationFinalMatch.player1_name 
                    : consolationFinalMatch.player2_name
                }
              : null;
            
            return consolationWinner ? (
              <div className="mt-6 bg-gradient-to-br from-teal-400 via-cyan-500 to-teal-600 p-6 rounded-xl shadow-lg text-center">
                <h4 className="text-xl font-black text-white mb-2">
                  🏅 מנצח בתנחומים 🏅
                </h4>
                <a 
                  href={`/profile/${consolationWinner.id}`}
                  className="text-2xl font-extrabold text-white hover:text-yellow-200 transition-colors"
                >
                  {consolationWinner.name}
                </a>
                <p className="text-white/90 mt-2">זוכה בפרס התנחומים + 50 נקודות בונוס!</p>
              </div>
            ) : null;
          })()}
        </div>
        </div>
      )}
    </div>
  );
};

export default TournamentBracketInteractive;

